// engine.ts — pure state logic for Ember Hollow.
// Contains: initial state factory, delta application, condensation for the model.
// No React, no I/O, no DOM access — fully testable in isolation.

// ── World topology constants ─────────────────────────────────────────────
//
// The world is a sparse 3D mask on cells keyed by "x,y,dimension,level".
// Each cell is either absent (unknown / open), "blocked" (void / outer wall),
// or `room:<id>` (an existing room occupies this cell — multi-cell rooms
// have one entry per cell).
//
// Per-scenario worldGen tunables (blockChancePerExit, blockSplatMaxCells)
// live on the server's Scenario; only the layerSemantics value is mirrored
// here because the engine must consult it to route legacy setLayer deltas
// and to back-fill positions on migration. Keep in sync with
// server/src/scenarios/*.ts. Unknown scenarios fall through to "none".

export type LayerSemantics = "none" | "spatial" | "dimensional";

export const LAYER_SEMANTICS_BY_SCENARIO: Record<string, LayerSemantics> = {
  ember_hollow: "none",
  verity_drift: "none",
  drowned_cathedral: "none",
  hollow_court: "none",
  salt_glass_tower: "spatial",
  watchmans_lantern: "none",
  veridian_bloom: "dimensional",
  the_greenway: "none",
  vaultsworn: "spatial",
  the_coldwater_line: "none",
  the_sleeping_floor: "dimensional",
};

export function getLayerSemantics(
  scenarioId: string | null | undefined,
): LayerSemantics {
  if (!scenarioId) return "none";
  return LAYER_SEMANTICS_BY_SCENARIO[scenarioId] ?? "none";
}

/**
 * Per-scenario block-roll knobs. Mirrors server/src/scenarios/*.ts so the
 * client engine can splat dead-ends locally (the server never runs
 * applyDelta). Keep in sync. Unknown scenarios fall through to a quiet
 * default (0.25 / 4).
 */
export const WORLD_GEN_BY_SCENARIO: Record<
  string,
  { blockChancePerExit: number; blockSplatMaxCells: number }
> = {
  ember_hollow: { blockChancePerExit: 0.30, blockSplatMaxCells: 4 },
  verity_drift: { blockChancePerExit: 0.20, blockSplatMaxCells: 4 },
  drowned_cathedral: { blockChancePerExit: 0.25, blockSplatMaxCells: 4 },
  hollow_court: { blockChancePerExit: 0.40, blockSplatMaxCells: 4 },
  salt_glass_tower: { blockChancePerExit: 0.50, blockSplatMaxCells: 4 },
  watchmans_lantern: { blockChancePerExit: 0.10, blockSplatMaxCells: 4 },
  veridian_bloom: { blockChancePerExit: 0.35, blockSplatMaxCells: 4 },
  the_greenway: { blockChancePerExit: 0.20, blockSplatMaxCells: 4 },
  vaultsworn: { blockChancePerExit: 0.55, blockSplatMaxCells: 4 },
  the_coldwater_line: { blockChancePerExit: 0.30, blockSplatMaxCells: 4 },
  the_sleeping_floor: { blockChancePerExit: 0.40, blockSplatMaxCells: 4 },
};

export function getWorldGen(
  scenarioId: string | null | undefined,
): { blockChancePerExit: number; blockSplatMaxCells: number } {
  if (!scenarioId) return { blockChancePerExit: 0.25, blockSplatMaxCells: 4 };
  return (
    WORLD_GEN_BY_SCENARIO[scenarioId] ?? {
      blockChancePerExit: 0.25,
      blockSplatMaxCells: 4,
    }
  );
}

export const CARDINAL_DIRECTIONS = [
  "north",
  "south",
  "east",
  "west",
  "up",
  "down",
] as const;
export type CardinalDirection = (typeof CARDINAL_DIRECTIONS)[number];

/**
 * Vector offset per cardinal direction. up / down don't change (x, y) — they
 * apply to `level` instead. The map's neighborhood-walk for `up` / `down`
 * uses (dimension, level) deltas of (0, 1) and (0, -1).
 */
export const DIRECTION_VECTORS: Record<
  CardinalDirection,
  { dx: number; dy: number; dLevel: number }
> = {
  north: { dx: 0, dy: -1, dLevel: 0 },
  south: { dx: 0, dy: 1, dLevel: 0 },
  east: { dx: 1, dy: 0, dLevel: 0 },
  west: { dx: -1, dy: 0, dLevel: 0 },
  up: { dx: 0, dy: 0, dLevel: 1 },
  down: { dx: 0, dy: 0, dLevel: -1 },
};

/** Cell-key format used in worldMask. Includes all four axes. */
export function cellKey(
  x: number,
  y: number,
  dimension: number,
  level: number,
): string {
  return `${x},${y},${dimension},${level}`;
}

// ── Types ────────────────────────────────────────────────────────────────

export type ItemLocation = "inventory" | "gone" | `room:${string}`;

export interface Item {
  id: string;
  name: string;
  description: string;
  location: ItemLocation | string;
  tags: string[];
}

export type Direction =
  | "north"
  | "south"
  | "east"
  | "west"
  | "up"
  | "down"
  | string;

export type RoomShape = "rect" | "cave" | "tunnel";

export interface Room {
  id: string;
  name: string;
  description: string;
  exits: Record<Direction, string | null>;
  items: string[];
  searchedSpots: string[];
  features: string[];
  visited: boolean;
  /**
   * Where this room sits on the world grid. Optional on the type so older
   * saves migrate cleanly; migrateState back-fills via BFS from the player's
   * current room. New rooms emitted by applyDelta always receive a position.
   */
  position?: {
    x: number;
    y: number;
    dimension: number;
    level: number;
  };
  /**
   * Visual shape hint emitted by the model. The engine stores it untouched;
   * the map UI uses it to pick a rendering style. Defaults to "rect" when
   * absent.
   */
  shape?: RoomShape;
  /**
   * How many adjacent cells this room occupies. 1..4. Defaults to 1 when
   * absent.
   */
  cellSpan?: number;
  /**
   * The cell-keys (in "x,y" form — dimension/level are taken from position)
   * this room occupies. For cellSpan === 1, a single entry; for longer
   * rooms (tunnels, halls), multiple. The engine fills this in on room
   * creation; migrateState back-fills single-cell rooms from position.
   */
  cells?: string[];
}

export interface Puzzle {
  id: string;
  description: string;
  solved: boolean;
  // The model is permitted to attach extra fields; we keep them.
  [key: string]: unknown;
}

export interface OrphanKey {
  id: string;
  itemTag?: string;
  hint?: string;
  [key: string]: unknown;
}

export interface OrphanLock {
  id: string;
  requiresItemTag?: string;
  hint?: string;
  [key: string]: unknown;
}

// ── Romance / companion types ────────────────────────────────────────────

export type PlayerGender = "male" | "female" | "non-binary" | null;

export interface PlayerIdentity {
  /** False until the setup screen submits (filled OR skipped). */
  setupComplete: boolean;
  /** null = "skip / gender-neutral". */
  gender: PlayerGender;
  /** null = unspecified; multi-element = randomized per NPC at introduction. */
  drawnTo: Exclude<PlayerGender, null>[] | null;
  /** Optional self-name; model may ask later or carry through. */
  name?: string;
  /**
   * Player's own age. Integer 17-90. Required after setupComplete = true
   * for newly-set-up players; legacy saves predating this field leave it
   * undefined and the prompt treats that as "narrator ignores". The
   * player can fill it in any time from the Player-identity menu item.
   */
  age?: number;
  /**
   * Minimum age the player is drawn to in romance candidates. Integer
   * 17-90. Defaults to 17 ("any adult"). Honoured by the model when
   * generating tracked companion NPCs; ignored for set-dressing.
   */
  drawnToAgeMin?: number;
  /**
   * Maximum age the player is drawn to in romance candidates. Integer
   * 17-90. Defaults to 90. Honoured by the model when generating
   * tracked companion NPCs; ignored for set-dressing.
   */
  drawnToAgeMax?: number;
  /**
   * Adaptive spice level 0..3. Defaults to 1 (mild). The model auto-
   * adjusts via stateDelta.setSpice based on the player's apparent
   * engagement with kink-themed beats. Players have no direct UI control
   * — adjustment is purely behavioural. Super-admins get an override
   * slider in the user menu for testing.
   *   0 vanilla — no kink beats at all
   *   1 mild    — rare surprise beats, gentle, fade-to-suggestion
   *   2 spicy   — more frequent, NPCs may initiate boldly
   *   3 forward — NPCs lean into kink themes, predatory tension on the table
   */
  spice: number;
}

export type NpcLocation = "with_player" | "absent" | `room:${string}` | string;

// ── NPC character-depth enums (set-once-permanent except openToShare) ──
// All 12 fields are optional on the wire so older saves don't break; the
// model is expected to fill them at first introduction for new NPCs.
//
// Bundle A — Intimacy variation: disposition, intimacyPreference, ageBand,
//   physique, distinguishingFeature
// Bundle B — Voice + presence: speechPattern, tell, humor
// Bundle C — Narrative hooks: hiddenSecret, trustScar, loyaltyPull
// Bundle D — Emergent openness: openToShare (shiftable via npcShiftOpenness)

export const NPC_DISPOSITIONS = [
  "flirty",
  "warm",
  "bold",
  "reserved",
  "cool",
  "guarded",
] as const;
export type NpcDisposition = (typeof NPC_DISPOSITIONS)[number];

export const NPC_INTIMACY_PREFS = [
  "yielding",
  "commanding",
  "switching",
  "tender",
] as const;
export type NpcIntimacyPreference = (typeof NPC_INTIMACY_PREFS)[number];

export const NPC_AGE_BANDS = ["young_adult", "mid_adult", "older"] as const;
export type NpcAgeBand = (typeof NPC_AGE_BANDS)[number];

export const NPC_PHYSIQUES = [
  "lithe",
  "broad",
  "full",
  "sturdy",
  "wiry",
  "willowy",
] as const;
export type NpcPhysique = (typeof NPC_PHYSIQUES)[number];

export const NPC_SPEECH_PATTERNS = [
  "formal",
  "informal",
  "archaic",
  "clipped",
  "verbose",
  "wry",
] as const;
export type NpcSpeechPattern = (typeof NPC_SPEECH_PATTERNS)[number];

export const NPC_HUMORS = [
  "dry",
  "ribald",
  "gallows",
  "gentle",
  "sarcastic",
  "earnest",
  "none",
] as const;
export type NpcHumor = (typeof NPC_HUMORS)[number];

export const NPC_OPEN_TO_SHARE = ["closed", "curious", "open"] as const;
export type NpcOpenToShare = (typeof NPC_OPEN_TO_SHARE)[number];

// ── Kink spectrum (controlled vocabulary, set-once-permanent) ─────────────
// An NPC may have 0..2 kinks drawn from this 8-tag vocabulary. Composes
// with intimacyPreference (which is HOW the NPC engages) — kinks are
// WHAT excites them specifically. Set at NPC creation via newNpcs.kinks,
// immutable after. Out-of-vocabulary strings are dropped on apply; the
// array is sliced to 2 entries to prevent model drift.

export const KINK_VALUES = [
  "dom",
  "sub",
  "taken",
  "voy",
  "exh",
  "play",
  "brat",
  "caretaker",
] as const;
export type KinkValue = (typeof KINK_VALUES)[number];

export interface Npc {
  id: string;
  name: string;
  description: string;
  location: NpcLocation;
  /** "male" | "female" | "non-binary"; resolved at introduction from playerIdentity.drawnTo. */
  gender?: Exclude<PlayerGender, null>;
  /** Loose archetype seed: "knows_estate" | "searching" | "shouldnt_be_here" | string. */
  archetype?: string;
  /** -3 hostile … +5 devoted; clamped on every change. */
  affinity: number;
  knowsAbout: string[];
  wants: string[];
  mood?: string | null;
  /** Unique, append-only. Drives "no repeats" — once "first_kiss" is here, it doesn't happen again. */
  bondMoments: string[];

  // ── Bundle A: Intimacy variation (set-once-permanent) ──────────────────
  /** How they approach affinity progression. See PROMPTING.md "DISPOSITION". */
  disposition?: NpcDisposition;
  /** What kind of intimacy they're drawn to once trust is real. Consensual. */
  intimacyPreference?: NpcIntimacyPreference;
  /** young_adult ≈ early/mid 20s; mid_adult ≈ 30s/early 40s; older ≈ 45+ */
  ageBand?: NpcAgeBand;
  physique?: NpcPhysique;
  /** Free-text ≤80 chars. ONE specific thing the player will remember. */
  distinguishingFeature?: string;

  // ── Bundle B: Voice + presence (set-once-permanent) ────────────────────
  speechPattern?: NpcSpeechPattern;
  /** Free-text ≤80 chars. ONE specific gesture/verbal tic. */
  tell?: string;
  humor?: NpcHumor;

  // ── Bundle C: Narrative hooks (set-once-permanent) ─────────────────────
  /** Free-text ≤200 chars. Model-internal until affinity ≥ +3. */
  hiddenSecret?: string;
  /** Free-text ≤200 chars. What makes them flinch under stress. */
  trustScar?: string;
  /** Free-text ≤200 chars. Who else they serve. Creates conflict choices. */
  loyaltyPull?: string;

  // ── Bundle D: Emergent openness (mutable via npcShiftOpenness) ─────────
  /** Default behaviour when undefined is "closed". Shifts via the stateDelta field. */
  openToShare?: NpcOpenToShare;

  // ── Bundle E: Kink spectrum (set-once-permanent) ───────────────────────
  /**
   * Up to 2 kink-spectrum tags drawn from the controlled vocabulary
   * (see KINK_VALUES). Set at NPC creation, immutable after. May be
   * absent (legacy saves or vanilla NPCs).
   */
  kinks?: string[];
}

export const AFFINITY_MIN = -3;
export const AFFINITY_MAX = 5;

// ── Guidance (adaptive narration) ─────────────────────────────────────────
//
// Per-story integer slider on [-2, +2] tracking how much hand-holding the
// narrator does. Three user-facing modes (Read / Classic / Hard) map to
// +2 / 0 / -2; +1 / -1 are reserved for a future auto-mode that watches
// player behaviour and steps the level smoothly.
//
// The level rides in the per-turn condensed state; the system prompt
// describes what each level means. The system prompt itself does NOT
// change per turn, so Anthropic's prompt cache stays warm.

export interface GuidanceState {
  /** Integer in [-2, 2]. Higher = more hand-holding; lower = expert. */
  level: number;
  /** "manual" today; "auto" reserved for the future heuristic scorer. */
  mode: "manual";
}

export const GUIDANCE_MIN = -2;
export const GUIDANCE_MAX = 2;

export type LogEntryKind = "input" | "narration" | "system" | "error";

/**
 * Immutable, append-only fact established in the course of a story. Lives
 * on `GameState.canon`; surfaced to the model on every turn via
 * `CondensedState.canon`. Engine auto-emits facts on bond moments, the
 * commitment beat (affinity ≥ +5), and puzzle solves; the model emits
 * narrative canon via `delta.addCanonFact`.
 *
 * Canon is monotonically append-only — no edits, no removals. If a fact
 * becomes wrong, a NEW clarifying fact is appended; the old one stays.
 */
export interface CanonFact {
  id: string;
  /** One-sentence statement of an established, irreversible story beat. */
  text: string;
  /** Turn the fact became canon. */
  turn: number;
  /**
   * Provenance — engine auto-emits or the model deliberately added it.
   *
   * `series_history` is the SERIES carryover source: facts seeded from a
   * PRIOR book when this story is a sequel (server/src/services/sequelStory.ts).
   * They are immutable established backstory the model must not contradict —
   * see the SERIES prompt section. The engine treats them like any other
   * canon (append-only, surfaced in full to the model); they just carry a
   * distinct source so the prompt can reason about them.
   */
  source:
    | "engine_bond"
    | "engine_commitment"
    | "engine_puzzle"
    | "model"
    | "series_history";
}

// ── Series / sequel continuity (mirrors server/src/types/series.ts) ────────
//
// Keep these in lock-step with the canonical server definitions in
// server/src/types/series.ts — they are the SHARED sequel contract. The
// engine only needs them for the seeded `seriesContext` on GameState (and,
// in Phase 3b, the Finale "next book" cards). The server owns the
// SequelOption → carryover-bundle spawn; the web side reads `seriesContext`
// back out via condenseStateForModel so the SERIES prompt section can act.

/** A book's structural relation to its predecessor in a series. */
export type SeriesStructure =
  | "standalone"
  | "episodic"
  | "arc"
  | "prequel"
  | "generational"
  | "sidestory";

/** A candidate "next book" direction (Phase 3b generates a slate of these). */
export interface SequelOption {
  id: string;
  structure: SeriesStructure;
  title: string;
  pitch: string;
  newTarget: string;
  playAs: string;
  carries: string[];
}

/**
 * The continuity bundle seeded onto a sequel's GameState. Present only when
 * a story is a sequel; absent (undefined) for standalone stories. The
 * prompt's SERIES section reads it (surfaced via condenseStateForModel) to
 * instruct the model how to honour the prior book per structure.
 */
export interface SeriesContext {
  structure: SeriesStructure;
  newTarget: string;
  carries: string[];
  priorProtagonist: string;
  bookNumber: number;
}

export interface LogEntry {
  kind: LogEntryKind;
  text: string;
  /**
   * UI-only field. Set on `kind === "error"` entries that the player can
   * retry — carries the original input text so the Retry button in
   * `LogLine` can resubmit it via `handleSubmit(undefined, pendingInput)`.
   * NEVER sent to the model: `condenseStateForModel` strips it from the
   * `recentLog` slice so the prompt's view of the log stays
   * byte-identical to what it was before this field existed.
   */
  pendingInput?: string;
}

export type GameOver = "win" | "death" | null;

export interface GameState {
  rooms: Record<string, Room>;
  items: Record<string, Item>;
  pendingPuzzles: Puzzle[];
  orphanKeys: OrphanKey[];
  orphanLocks: OrphanLock[];
  inventory: string[];
  currentRoom: string;
  turnCount: number;
  log: LogEntry[];
  gameOver: GameOver;
  /** Player self-identity — set once via the CharacterSetup screen. */
  player: PlayerIdentity;
  /** Companion candidates + other NPCs the world is tracking. */
  npcs: Record<string, Npc>;
  /** Adaptive-narration slider. Default {level:0, mode:"manual"}. */
  guidance: GuidanceState;
  /**
   * Legacy generic "depth/plane/tier". Kept for back-compat with saves that
   * pre-date the dimension/level split (2026-05-13). New code should read
   * `dimension` or `level` (or both) according to the scenario's
   * `layerSemantics`. `migrateState` back-fills this onto the correct axis.
   */
  layer?: number;
  /**
   * D.L coordinates — two integer axes:
   *
   *   dimension — parallel realities (dream depth, simulation plane).
   *               Cell coordinates reset on a dimensional shift.
   *   level     — spatial floor within a dimension (stairs, lifts).
   *               Cell coordinates carry across level changes.
   *
   * Both default to 0. Scenarios with layerSemantics "none" leave both
   * untouched; "spatial" scenarios move on `level`; "dimensional" scenarios
   * move on `dimension`. The legacy `setLayer` delta routes to one of these
   * based on the scenario, so existing saves migrate transparently.
   */
  dimension: number;
  level: number;
  /**
   * Sparse 3D mask of buildable space. Keys are "x,y,dimension,level"
   * (see `cellKey`); values are either "blocked" (void / outer wall —
   * the model is told never to build here) or `room:<id>` (an existing
   * room occupies this cell). Absent keys are unknown / open — a future
   * room may be built there.
   *
   * Cells are blocked DYNAMICALLY when rooms are created — each un-used
   * cardinal direction rolls scenario.worldGen.blockChancePerExit and on
   * a hit splats 1..blockSplatMaxCells in that direction. The RNG is
   * seeded per (storyId, cellKey) so save/reload reproduces the same
   * layout.
   *
   * Multi-cell rooms have one `room:<id>` entry per cell they occupy.
   */
  worldMask: Record<string, "blocked" | string>;
  /**
   * Currently-playing ambient bed (continuous loop). Set via the model's
   * `setAmbient` delta; null while no bed is playing. Persisted so the
   * model can see what's already in place via `condenseStateForModel` and
   * avoid pointless re-sets. Phase A audio.
   */
  currentAmbient: string | null;
  /**
   * Currently-playing sporadic punctuation (randomised-interval one-shot
   * scheduler). Set via the model's `setSporadic` delta; null while no
   * sporadic is scheduled. Same persistence rationale as `currentAmbient`.
   *
   * NOTE: there is no corresponding `currentPlaySfx` — `playSfx` is a
   * transient delta-only signal fired once by the React audio hook and
   * never stored in GameState. See `StateDelta.playSfx`.
   */
  currentSporadic: string | null;
  /**
   * Immutable, append-only ledger of established story beats. Engine
   * auto-emits canon facts for mechanical events (bond moments, the
   * commitment beat at affinity ≥ +5, puzzle solves); the model emits
   * narrative canon via `delta.addCanonFact`. Surfaced to the model in
   * FULL on every turn via `CondensedState.canon` — these are
   * load-bearing and never expire.
   *
   * No cap on length: per-turn growth is bounded by the schema (one
   * model-emitted fact per turn) plus rare engine triggers. A 200-turn
   * romance might accumulate ~30-50 facts; trivial token cost.
   *
   * Legacy saves missing this field are coerced to `[]` by `migrateState`.
   */
  canon: CanonFact[];
  /**
   * Per-story content-register high-water-mark. Defaults "default" on
   * every new story. Monotonic upward: once a story drifts to "mature"
   * via the adaptive intimacy mechanic, it stays mature (the canon's
   * already there). Sharing to the public library is gated on
   * "default" only — see server/src/routes/stories.ts share endpoint.
   *
   * Drift is admin-gated server-side during testing: non-admin users'
   * prompts run with the strict fade-default rule and the model never
   * emits setContentMark. The engine doesn't enforce admin-ness — that
   * gate lives in routes/turn.ts as a backstop on the delta.
   */
  contentMark: "default" | "mature";
  /**
   * SERIES continuity bundle — present ONLY when this story is a sequel
   * (spawned via server/src/services/sequelStory.ts). Undefined / absent for
   * standalone stories (the common case). Seeded at spawn time alongside the
   * `series_history` canon facts; never mutated by applyDelta. Surfaced to
   * the model via condenseStateForModel so the prompt's SERIES section can
   * instruct it on how to honour the prior book for this book's structure.
   *
   * Optional + guarded everywhere — legacy / standalone saves simply don't
   * carry it, and migrateState leaves it undefined.
   */
  seriesContext?: SeriesContext;
}

// Delta returned by the model. Every field is optional — applyDelta is defensive.
export interface StateDelta {
  newRooms?: Array<{
    id: string;
    name?: string;
    description?: string;
    exits?: Record<string, string | null>;
    items?: string[];
    features?: string[];
    /** Visual shape hint. Defaults to "rect" if absent or invalid. */
    shape?: RoomShape | string;
    /** How many adjacent cells the room occupies. 1..4. Defaults to 1. */
    cellSpan?: number;
    /**
     * The cardinal direction the player arrived from. The engine uses this
     * to place the new room one cell off the current room. Null / omitted
     * for teleports or the very first room.
     */
    arrivalDirection?: CardinalDirection | string | null;
  }>;
  connectExits?: Array<{ fromRoom: string; direction: string; toRoom: string }>;
  newItems?: Array<Partial<Item> & { id: string; location?: string }>;
  moveItems?: Array<{ itemId: string; to: string }>;
  markSearched?: Array<{ room: string; spot: string }>;
  newPuzzles?: Array<Omit<Puzzle, "solved"> & { solved?: boolean }>;
  solvePuzzles?: Array<{ id: string; consumeItem?: string }>;
  queueOrphanKey?: OrphanKey;
  queueOrphanLock?: OrphanLock;
  moveToRoom?: string;
  /**
   * Optional ordered list of room ids the player traversed THIS turn —
   * a "chained move" hint for chained input ("go north then east"), an
   * NPC leading the player down a corridor, or narrative elision ("you
   * follow him through the bay and into the loading deck"). The path's
   * first id is the room the player was in at the start of the turn;
   * the last id equals `moveToRoom`.
   *
   * UI-ONLY. `applyDelta` does NOT store this in GameState — it's a
   * transient hint consumed by App.tsx, which validates the path against
   * the post-delta state and threads it to the map for path-stepping
   * animation. The field is declared here so the StateDelta type stays a
   * faithful mirror of the server prompt's schema. Single-hop moves omit
   * it entirely.
   */
  movementPath?: string[];

  // ── Romance / companion deltas ─────────────────────────────────────────
  newNpcs?: Array<Partial<Npc> & { id: string; name: string }>;
  npcLocationChange?: Array<{ id: string; to: NpcLocation }>;
  /** Incremental affinity nudges. Total for the turn is summed; applyDelta clamps. */
  npcAffinityDelta?: Array<{ id: string; delta: number; reason?: string }>;
  npcKnowsAbout?: Array<{ id: string; facts: string[] }>;
  npcMood?: Array<{ id: string; mood: string }>;
  npcWants?: Array<{ id: string; wants: string[] }>;
  /** One-shot intimate beats (kiss, confession, vow). De-duplicated per (id, moment). */
  addBondMoment?: Array<{ id: string; moment: string }>;
  /**
   * Shift an existing NPC's openToShare state. The ONLY persistent NPC field
   * that's mutable post-creation — every other character-depth field is
   * set-once-permanent on the first newNpcs entry for that id.
   *
   * `reason` is purely audit/debug; the model surfaces the shift to the
   * player through narration, not as a UI event. See PROMPTING.md
   * "SHARING / OPENNESS".
   */
  npcShiftOpenness?: Array<{
    id: string;
    to: NpcOpenToShare;
    reason?: string;
  }>;
  /** Optional: lets the model record a self-name the player gives in dialogue. */
  setPlayerName?: string;
  /**
   * Optional: model-emitted self-rename of the story. App-layer effect, NOT
   * a state mutation — story.name lives on the server in the stories table
   * (surfaced via currentStory in App.tsx), not anywhere in GameState. The
   * App.tsx success path reads result.stateDelta.setStoryName from the turn
   * response and calls renameStory() if present and non-empty. applyDelta
   * intentionally does nothing with this field; it's declared here purely so
   * the StateDelta type stays a faithful mirror of the server prompt's
   * schema. See PROMPTING.md "STORY NAME".
   */
  setStoryName?: string;
  /**
   * Optional: model-emitted self-adjustment of the guidance level. Integer
   * in [-2, 2]; out-of-range or non-integer values are clamped/rounded by
   * applyDelta. Use sparingly — the manual UI override is the authoritative
   * source. See PROMPTING.md "GUIDANCE LEVEL".
   */
  setGuidance?: number;
  /**
   * Optional. Model-emitted auto-adjustment of state.player.spice based
   * on the player's engagement with kink beats. Integer in [0, 3]. The
   * engine clamps to that range AND caps the per-turn change at ±1
   * (so a runaway model can't jump from 0 to 3 in one turn). Sustained
   * negative signals (explicit refusal, withdrawal) drive it down;
   * sustained positive engagement drives it up.
   */
  setSpice?: number;
  /**
   * @deprecated Prefer `setDimension` or `setLevel`. Kept as a back-compat
   * alias for saves predating the D.L split (2026-05-13). When applyDelta
   * receives setLayer, it routes the value to either setDimension or
   * setLevel based on the scenario's layerSemantics.
   */
  setLayer?: number;
  /**
   * Optional: model-emitted DIMENSIONAL shift — parallel reality, dream
   * depth, simulation plane. Integer. Cell coordinates reset on a
   * dimensional shift; the engine handles the reset.
   */
  setDimension?: number;
  /**
   * Optional: model-emitted SPATIAL floor change within the current
   * dimension. Integer. +1 = one floor up, -1 = one floor down. Cell
   * coordinates carry across the change.
   */
  setLevel?: number;
  /**
   * Optional: model-emitted ambient-bed change. snake_case tag (resolved
   * to a file via web/src/audio/catalogue.ts), or null/empty-string to
   * stop the current bed. Omitted entirely → leave currentAmbient as-is.
   * applyDelta writes the new value into GameState.currentAmbient; the
   * React audio hook reads it and crossfades. Defensive: non-string
   * non-null values are ignored.
   */
  setAmbient?: string | null;
  /**
   * Optional: model-emitted sporadic punctuation change. Same shape as
   * setAmbient; resolves to a file + an interval range in the catalogue.
   * Sporadics where minIntervalMs === maxIntervalMs give a regular
   * cadence (clock tick, tap drip).
   */
  setSporadic?: string | null;
  /**
   * Optional: TRANSIENT one-shot sound for this turn. snake_case tag, or
   * null/empty/omitted for "nothing this turn". UNLIKE setAmbient and
   * setSporadic, this field is NEVER persisted into GameState — the
   * React audio hook reads it directly off the delta and fires-and-
   * forgets a single play. applyDelta deliberately does nothing with it.
   */
  playSfx?: string | null;
  /**
   * Optional. Model-emitted append to the canon ledger when something
   * narratively load-bearing happens (a backstory reveal, a promise, a
   * death, a vow, a name learned, a secret shared). NOT for routine
   * observations. Max 200 chars; longer entries are clamped on apply.
   * One per turn (single object, not array). Empty / malformed entries
   * are silently dropped.
   */
  addCanonFact?: { text: string };
  /**
   * Optional. Model-emitted high-water-mark elevation. Only valid value
   * is "mature" — "default" can't be re-set (monotonic upward). Engine
   * silently ignores any value other than "mature" and ignores it if
   * the current state is already "mature".
   *
   * Server backstop (routes/turn.ts) strips this delta entirely from
   * non-admin user turn responses before applyDelta runs, so the engine
   * never sees a setContentMark from a non-admin context. The engine's
   * own validation here is belt-and-braces.
   */
  setContentMark?: "mature";
}

/**
 * Description of what lies in a single cardinal direction from the player's
 * current cell. Surfaced in CondensedState.neighborhood.
 *
 *   - "blocked"  — void / outer wall. The model is told never to build here.
 *   - "existing" — a known room. The roomId is provided so the model can
 *                  reference it.
 *   - "open"     — unknown space. maxSize reflects how many open cells
 *                  extend in that direction (small = 1, medium = 2-3,
 *                  large = 4+). The model uses this to choose cellSpan
 *                  without overflowing into blocked cells.
 */
export type ExitInfo =
  | { kind: "blocked" }
  | { kind: "existing"; roomId: string }
  | { kind: "open"; maxSize: "small" | "medium" | "large" };

// Condensed state shape sent to the model. Server prompt expects this shape.
export interface CondensedState {
  currentRoom: Room | undefined;
  adjacentRooms: Record<string, { id: string; name: string; visited: boolean }>;
  inventory: Item[];
  pendingPuzzles: Puzzle[];
  orphanKeys: OrphanKey[];
  orphanLocks: OrphanLock[];
  /**
   * Every item in the world (inventory + every room + 'gone'). Compact —
   * no description text. Lets the model spot collisions before emitting
   * newItems with a name that already exists. Gone items are included
   * because the player may still refer to them in the log, and emitting
   * a new item with a duplicate id silently fails in applyDelta.
   */
  worldItems: Array<{
    id: string;
    name: string;
    /** "inventory" | "gone" | "room:<id>" */
    location: string;
    tags: string[];
  }>;
  /**
   * Every room the player has discovered. Replaces the bare-IDs list
   * `allKnownRoomIds` with named entries that ALSO carry the room's named
   * features so cross-room collisions are visible. Description text is
   * intentionally omitted — that's where the bulk of the token cost would
   * land. Adjacent rooms still get their own richer entry via
   * `adjacentRooms`.
   *
   * Position + exits ship the canonical topology graph to the model so it
   * can reason spatially without re-deriving the world from narration.
   * `position` is always present; legacy saves missing a position fall back
   * to (0, 0, state.dimension, state.level). `exits` is always a complete
   * 6-direction object — directions with no connection are explicitly
   * `null` so the model never has to guess.
   */
  knownRooms: Array<{
    id: string;
    name: string;
    visited: boolean;
    /** Searchable named things in this room. */
    features: string[];
    position: { x: number; y: number; dimension: number; level: number };
    exits: Record<CardinalDirection, string | null>;
  }>;
  /**
   * Flat list of every named, searchable feature across all rooms.
   * Pre-flattened for fast collision-check by the model — saves it from
   * having to walk knownRooms[].features. Overlaps with knownRooms
   * contents; that's deliberate redundancy for prompt readability.
   */
  worldNamedFeatures: Array<{
    roomId: string;
    feature: string;
  }>;
  turnCount: number;
  /**
   * Last few log entries, mapped to a strict { kind, text } shape. The
   * source `LogEntry` may carry a UI-only `pendingInput` field (see its
   * declaration); the strip here guarantees that field never reaches the
   * model.
   */
  recentLog: Array<{ kind: LogEntryKind; text: string }>;
  /**
   * Player identity reshaped for the model. Flat `drawnToAgeMin` /
   * `drawnToAgeMax` from state are folded into a single `drawnToAge`
   * object with `min` / `max` to match the wording in BASE_PROMPT.
   * `age` is omitted entirely when the player hasn't provided one
   * (legacy saves) — the prompt treats absent age as "narrator ignores".
   */
  player: Omit<PlayerIdentity, "drawnToAgeMin" | "drawnToAgeMax"> & {
    drawnToAge: { min: number; max: number };
  };
  /** Full NPC objects for those in the current room or with_player. */
  npcsInScene: Npc[];
  /**
   * Brief refs for NPCs offstage. Includes `disposition`, `humor`, and
   * `openToShare` in addition to the basic identity fields — the model
   * needs these for cross-NPC awareness, especially the social-landscape
   * read that drives the openness mechanic. Narrative-material fields
   * (hiddenSecret, trustScar, loyaltyPull, distinguishingFeature, tell)
   * are intentionally OFF the summary — they're per-scene material, not
   * background awareness.
   *
   * `currentLocation` is the raw `state.npcs[id].location` string —
   * `"room:<id>"` | `"with_player"` | `"absent"`. Without this, an NPC
   * left behind in another room many turns ago has no structural anchor
   * for where they actually are, and the model drifts them back into
   * the player's scene narratively. The STATE FIDELITY rule in the
   * prompt forbids that — and this field is what makes the rule
   * enforceable.
   */
  npcsKnown: Array<
    Pick<
      Npc,
      | "id"
      | "name"
      | "location"
      | "affinity"
      | "mood"
      | "gender"
      | "disposition"
      | "humor"
      | "openToShare"
      | "kinks"
    > & { currentLocation: string }
  >;
  /**
   * Adaptive-narration level in [-2, 2]. The model's prompt explains what
   * each value means. `mode` is omitted — the model only needs the number.
   */
  guidance: { level: number };
  /**
   * Generic depth/plane/tier integer. Default 0 for scenarios that don't
   * opt in. Kept for back-compat with saves predating dimension/level.
   * Mirrors whichever of dimension/level is non-zero (per layerSemantics).
   */
  layer: number;
  /** Current dimension (parallel reality axis). 0 by default. */
  dimension: number;
  /** Current spatial floor (within the dimension). 0 by default. */
  level: number;
  /**
   * Spatial context for the current room — coordinates and shape, plus
   * what's adjacent in every cardinal direction. The model uses this to
   * choose where new rooms go and how big they should be.
   */
  neighborhood: {
    current: {
      cell: [number, number];
      dimension: number;
      level: number;
      shape: RoomShape;
      cellSpan: number;
    };
    exits: Record<CardinalDirection, ExitInfo>;
  };
  /**
   * What ambient bed is currently playing, if any. Lets the model see
   * what's already in place and avoid pointless re-sets. Phase A audio.
   */
  currentAmbient: string | null;
  /**
   * What sporadic punctuation is currently scheduled, if any. Same
   * rationale as currentAmbient. playSfx is transient and NOT shipped
   * back to the model — see StateDelta.playSfx.
   */
  currentSporadic: string | null;
  /**
   * Immutable facts established over the course of this story. Includes
   * engine-auto-emitted beats (bond moments, commitment, puzzle solves)
   * and model-emitted facts via addCanonFact. Always in full — these
   * are load-bearing and never expire.
   */
  canon: CanonFact[];
  /**
   * Content-register high-water-mark for the story. "default" by default;
   * flips to "mature" via the adaptive intimacy mechanic and stays.
   * Surfaced to the model on every turn so a story that has already
   * drifted into the mature register keeps its established voice on
   * subsequent turns (within-story continuity). Legacy saves missing
   * the field surface as "default"; migrateState coerces on hydrate.
   */
  contentMark: "default" | "mature";
  /**
   * SERIES continuity bundle — present ONLY when this story is a sequel.
   * Omitted entirely for standalone stories so the prompt's SERIES section
   * stays inert (it's gated on the presence of this block). When present,
   * the `series_history` canon facts in the `canon` array above are the
   * immutable backstory of the prior book(s); this block tells the model
   * how to honour them per structure. See the SERIES prompt section.
   */
  seriesContext?: SeriesContext;
  /**
   * Story-arc pacing signals. Drives how aggressively the model converges
   * on the engagement-hook barrier and offers an ending. See STORY ARC
   * section of the prompt for behaviour per state.
   */
  arc: {
    climaxState: ClimaxState;
    estimatedRemainingTurns: number;
    /**
     * True when the story is genuinely satisfiable REGARDLESS of turn
     * count (a +5 companion, OR puzzles solved + mystery established +
     * no dangling orphan lock). When true, the prompt's STORY ARC section
     * tells the model to PROACTIVELY OFFER the player an ending this turn.
     * Computed by computeEndingReadiness — outbound only; the model never
     * echoes it back, so there is no applyDelta handler.
     */
    endingReady: boolean;
    /**
     * Short human-readable strings naming WHY the story is ending-ready,
     * so the model can frame the offer in-fiction. Empty when not ready.
     */
    endingReadyReasons: string[];
    /**
     * Count of load-bearing canon facts the story has accumulated, EXCLUDING
     * `series_history` seeds (so a sequel's seeded prior-book history does not
     * mask a spineless current book). A spine indicator: a near-empty ledger
     * past the early phase means the central mystery was never established and
     * the story is drifting. Outbound-only, derived — the model never echoes
     * it back, so there is no applyDelta handler (same treatment as
     * endingReady). The prompt's STORY ARC "SPINE CHECK" rule decides what to
     * do about a low count; the engine only emits the raw number.
     */
    canonFactCount: number;
  };
}

// ── Factory ──────────────────────────────────────────────────────────────

/**
 * Placeholder initial state used ONLY by App.tsx's initial useState before
 * the boot tree hydrates a real story from the server. The user never sees
 * this — by the time the game UI renders, server state has replaced it.
 *
 * Per-scenario opening rooms live server-side on each scenario's
 * `openingRoom` + `openingItem` fields (server/src/scenarios/<id>.ts). The
 * server's services/initialState.ts builds the real fresh state from them
 * for both POST /api/stories and POST /api/stories/:id/restart, and the
 * web client hydrates from the response.
 *
 * Intentionally scenario-agnostic. Keep it as a stable threshold/lantern
 * shape so the type sits neatly with the rest of the engine; don't try to
 * make it scenario-aware on the client (the server is authoritative).
 */
export function makeInitialState(): GameState {
  return {
    rooms: {
      threshold: {
        id: "threshold",
        name: "The Threshold",
        description:
          "You stand at a wrought-iron gate, half-swallowed by ivy. Beyond it, a flagstone path winds toward a manor that lists slightly, as if exhausted by its own history. A lantern, recently lit, hangs from a hook beside you. The air smells of woodsmoke and rain.",
        exits: { north: null, south: null, east: null, west: null },
        items: ["lantern"],
        searchedSpots: [],
        features: ["the wrought-iron gate", "the ivy", "the lantern hook"],
        visited: true,
        position: { x: 0, y: 0, dimension: 0, level: 0 },
        shape: "rect",
        cellSpan: 1,
        cells: ["0,0"],
      },
    },
    items: {
      lantern: {
        id: "lantern",
        name: "a brass lantern",
        description:
          "A brass lantern, warm in your hand. Its flame burns steady.",
        location: "room:threshold",
        tags: ["light", "metal"],
      },
    },
    pendingPuzzles: [],
    orphanKeys: [],
    orphanLocks: [],
    inventory: [],
    currentRoom: "threshold",
    turnCount: 0,
    log: [
      {
        kind: "narration",
        text:
          "You stand at a wrought-iron gate, half-swallowed by ivy. Beyond it, a flagstone path winds toward a manor that lists slightly, as if exhausted by its own history. A lantern, recently lit, hangs from a hook beside you. The air smells of woodsmoke and rain.",
      },
      {
        kind: "system",
        text: "Try: look, take lantern, go north, search ivy, inventory, talk to <name>, help",
      },
    ],
    gameOver: null,
    player: {
      setupComplete: false,
      gender: null,
      drawnTo: null,
      // Age starts undefined — CharacterSetup makes it a required field
      // before setupComplete flips true. Drawn-to range defaults to the
      // full adult span (17..90), which is equivalent to "no preference".
      age: undefined,
      drawnToAgeMin: 17,
      drawnToAgeMax: 90,
      // Adaptive spice defaults to 1 (mild). The model auto-adjusts via
      // setSpice based on engagement; players never see a UI control.
      spice: 1,
    },
    npcs: {},
    // New players default to Story Mode (+2). Subsequent stories inherit
    // the player's last-used level via App.tsx lastKnownGuidance.
    guidance: { level: 2, mode: "manual" },
    layer: 0,
    dimension: 0,
    level: 0,
    worldMask: { "0,0,0,0": "room:threshold" },
    currentAmbient: null,
    currentSporadic: null,
    // Canon ledger starts empty. Engine auto-emits on bond moments, the
    // commitment beat at affinity ≥ +5, and puzzle solves; model emits
    // narrative canon via delta.addCanonFact.
    canon: [],
    // Content-register high-water-mark. Every fresh story starts at
    // "default" — the standard fade-default register. Only ever flips
    // to "mature" via the model's setContentMark delta on an admin's
    // turn (see routes/turn.ts admin gate + backstop). Monotonic.
    contentMark: "default",
  };
}

// ── Guidance helpers ─────────────────────────────────────────────────────

/**
 * Coerce an arbitrary value to a valid guidance level: integer, clamped
 * to [GUIDANCE_MIN, GUIDANCE_MAX]. Returns null if the input can't be
 * meaningfully coerced (NaN, Infinity, non-numeric).
 */
function coerceGuidanceLevel(raw: unknown): number | null {
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n)) return null;
  const rounded = Math.round(n);
  return Math.max(GUIDANCE_MIN, Math.min(GUIDANCE_MAX, rounded));
}

/**
 * Set the player's adaptive-narration level. Clamps to [-2, 2] and
 * rounds. Pure — returns a new state. The UI calls this when the
 * player picks a mode from the user-chip submenu or types
 * `guidance read` / `classic` / `hard`.
 */
export function setGuidanceLevel(state: GameState, level: number): GameState {
  const next = coerceGuidanceLevel(level);
  if (next === null) return state;
  const current = state.guidance ?? { level: 0, mode: "manual" as const };
  return {
    ...state,
    guidance: { level: next, mode: current.mode ?? "manual" },
  };
}

/**
 * Map an internal guidance level to the i18n string key for its named
 * mode. Callers in App.tsx wrap this in `t("game:guidance.<key>")` to
 * resolve to a localised display string. Keys cover all 5 levels
 * (story / guided / classic / sparse / expert) — one named mode per
 * level, no more "+ / -" suffix shimming since the dial is now fully
 * five-position by design.
 */
export function guidanceLabel(level: number): string {
  const n = coerceGuidanceLevel(level) ?? 0;
  if (n >= 2) return "story";
  if (n === 1) return "guided";
  if (n === 0) return "classic";
  if (n === -1) return "sparse";
  return "expert";
}

// ── Spice helpers (adaptive kink-engagement dial) ─────────────────────────
//
// `state.player.spice` is an integer in [0, 3] that gates how often / how
// boldly NPCs surface kink-themed beats. The model auto-adjusts via the
// `setSpice` delta based on observed engagement; super-admins can override
// directly via the user-menu slider for testing.

export const SPICE_MIN = 0;
export const SPICE_MAX = 3;

/**
 * Coerce an arbitrary value to a valid spice level: integer, clamped to
 * [SPICE_MIN, SPICE_MAX]. Returns null if the input can't be meaningfully
 * coerced (NaN, Infinity, non-numeric).
 */
function coerceSpiceLevel(v: unknown): number | null {
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  const n = Math.round(v);
  if (!Number.isInteger(n)) return null;
  return Math.max(SPICE_MIN, Math.min(SPICE_MAX, n));
}

/**
 * Set the player's spice level directly. Clamps to [0, 3] and rounds.
 * Pure — returns a new state. Used by the super-admin override slider
 * in the user menu; the model never calls this directly (it emits the
 * `setSpice` delta, which applyDelta processes with a per-turn change cap).
 */
export function setSpiceLevel(state: GameState, level: number): GameState {
  const next = coerceSpiceLevel(level);
  if (next == null) return state;
  return { ...state, player: { ...state.player, spice: next } };
}

/**
 * Forward-compatible state migration. Older saves don't have `player`,
 * `npcs`, `worldMask`, or per-room `position` — patch them in defensively
 * when hydrating from /api/save. Pure; mutates a deep copy.
 *
 * `scenarioId` is consulted (via `getLayerSemantics`) to route a legacy
 * single `state.layer` integer onto either `dimension` or `level`. If
 * omitted, the engine assumes "none" — both axes stay 0 and any legacy
 * layer integer is preserved on `state.layer` only.
 */
export function migrateState(
  raw: unknown,
  scenarioId?: string | null,
): GameState {
  const base = makeInitialState();
  if (!raw || typeof raw !== "object") return base;
  const s = raw as Partial<GameState> & Record<string, unknown>;

  // Resolve the legacy state.layer integer (if any) onto the new D.L axes.
  // Saves that already wrote dimension/level keep those values; saves that
  // only carry state.layer route via layerSemantics.
  const semantics = getLayerSemantics(scenarioId);
  const rawLayer = (s as { layer?: unknown }).layer;
  const legacyLayer =
    typeof rawLayer === "number" && Number.isFinite(rawLayer)
      ? Math.trunc(rawLayer)
      : 0;
  const rawDimension = (s as { dimension?: unknown }).dimension;
  const rawLevel = (s as { level?: unknown }).level;
  let dimension =
    typeof rawDimension === "number" && Number.isFinite(rawDimension)
      ? Math.trunc(rawDimension)
      : 0;
  let level =
    typeof rawLevel === "number" && Number.isFinite(rawLevel)
      ? Math.trunc(rawLevel)
      : 0;
  if (dimension === 0 && level === 0 && legacyLayer !== 0) {
    if (semantics === "spatial") {
      level = legacyLayer;
    } else if (semantics === "dimensional") {
      dimension = legacyLayer;
    }
  }

  const rooms = (s.rooms as GameState["rooms"]) ?? base.rooms;
  const items = (s.items as GameState["items"]) ?? base.items;
  const inventory = Array.isArray(s.inventory) ? s.inventory : [];
  const currentRoom =
    typeof s.currentRoom === "string" ? s.currentRoom : base.currentRoom;

  // Back-fill room positions / shape / cellSpan / cells. Walk the exit graph
  // BFS from the current room, assigning (0, 0, dimension, level) to it and
  // offsetting each cardinal neighbour. Loop-closure keeps the first-assigned
  // coordinates. Rooms NOT reachable from the current room via cardinals —
  // typical of legacy multi-layer stories where setLayer was a non-spatial
  // delta — form disconnected clusters in the exit graph; each cluster is
  // placed on its own synthetic axis value (level for "spatial" / "none"
  // scenarios, dimension for "dimensional") so the level/dimension switcher
  // surfaces each historical layer as a separate plane.
  const backfilledRooms = backfillRoomPositions(
    rooms,
    currentRoom,
    dimension,
    level,
    semantics,
  );

  // Build worldMask from the rooms we have. Existing mask entries are kept
  // (so blocked cells from prior turns survive); each room contributes its
  // cells. Saves that predate worldMask start fresh and gain blocks only
  // as new rooms are created.
  const rawMask = (s as { worldMask?: unknown }).worldMask;
  const worldMask: GameState["worldMask"] =
    rawMask && typeof rawMask === "object" && !Array.isArray(rawMask)
      ? { ...(rawMask as Record<string, "blocked" | string>) }
      : {};
  for (const room of Object.values(backfilledRooms)) {
    if (!room.position || !room.cells) continue;
    for (const xy of room.cells) {
      const [xs, ys] = xy.split(",");
      const x = Number(xs);
      const y = Number(ys);
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      const key = cellKey(x, y, room.position.dimension, room.position.level);
      // Don't overwrite a blocked entry — but room:<id> entries are fine
      // to overwrite (last-write-wins on duplicate cell claims).
      if (worldMask[key] !== "blocked") {
        worldMask[key] = `room:${room.id}`;
      }
    }
  }

  return {
    ...base,
    ...s,
    rooms: backfilledRooms,
    items,
    pendingPuzzles: Array.isArray(s.pendingPuzzles) ? s.pendingPuzzles : [],
    orphanKeys: Array.isArray(s.orphanKeys) ? s.orphanKeys : [],
    orphanLocks: Array.isArray(s.orphanLocks) ? s.orphanLocks : [],
    inventory,
    currentRoom,
    turnCount: typeof s.turnCount === "number" ? s.turnCount : 0,
    log: Array.isArray(s.log) ? s.log : base.log,
    gameOver: (s.gameOver as GameOver) ?? null,
    player:
      s.player && typeof s.player === "object"
        ? (() => {
            const p = s.player as PlayerIdentity;
            // Age + drawn-to age range may be absent on legacy saves.
            // Coerce defensively: integers in [17,90] or undefined for
            // age; defaults 17/90 for the range. Don't flip
            // setupComplete back to false — legacy users who completed
            // gender-only setup stay complete; they can fill age later
            // from the Player-identity menu item.
            const ageRaw = p.age;
            const age =
              typeof ageRaw === "number" && Number.isFinite(ageRaw)
                ? Math.max(17, Math.min(90, Math.trunc(ageRaw)))
                : undefined;
            const minRaw = p.drawnToAgeMin;
            const maxRaw = p.drawnToAgeMax;
            let minAge =
              typeof minRaw === "number" && Number.isFinite(minRaw)
                ? Math.max(17, Math.min(90, Math.trunc(minRaw)))
                : 17;
            let maxAge =
              typeof maxRaw === "number" && Number.isFinite(maxRaw)
                ? Math.max(17, Math.min(90, Math.trunc(maxRaw)))
                : 90;
            if (minAge > maxAge) {
              const swap = minAge;
              minAge = maxAge;
              maxAge = swap;
            }
            // Spice — legacy saves predating this field default to 1 (mild).
            // NEVER retroactively shift an existing save's spice value if it
            // is already present and valid; the player's adapted level is
            // sovereign across migrations.
            const spice = coerceSpiceLevel((p as { spice?: unknown }).spice);
            return {
              setupComplete: Boolean(p.setupComplete),
              gender: (p.gender ?? null) as PlayerGender,
              drawnTo:
                Array.isArray(p.drawnTo) &&
                (p.drawnTo as unknown[]).length > 0
                  ? (p.drawnTo as Exclude<PlayerGender, null>[])
                  : null,
              name: p.name,
              age,
              drawnToAgeMin: minAge,
              drawnToAgeMax: maxAge,
              spice: spice ?? 1,
            };
          })()
        : base.player,
    npcs: (s.npcs && typeof s.npcs === "object" ? (s.npcs as GameState["npcs"]) : {}),
    guidance: (() => {
      // Idempotent: if a valid guidance object is already present, keep it.
      // Otherwise back-fill the default. Older saves predate this field.
      // Legacy saves: never retroactively shift voice. New default (+2)
      // only applies to fresh makeInitialState — this fallback fires for
      // saves that predate the guidance field entirely (level 0 = the
      // canonical IF register the save was written in).
      const raw = (s as { guidance?: unknown }).guidance;
      if (raw && typeof raw === "object") {
        const r = raw as Partial<GuidanceState>;
        const lvl = coerceGuidanceLevel(r.level);
        if (lvl !== null) {
          return { level: lvl, mode: "manual" };
        }
      }
      return { level: 0, mode: "manual" };
    })(),
    layer: legacyLayer,
    dimension,
    level,
    worldMask,
    // Phase A audio fields. Older saves predate them; coerce missing or
    // malformed values to null (silent). Empty string also normalises to
    // null so the audio hook's "non-null tag" branch is unambiguous.
    currentAmbient: (() => {
      const raw = (s as { currentAmbient?: unknown }).currentAmbient;
      if (typeof raw === "string" && raw.length > 0) return raw;
      return null;
    })(),
    currentSporadic: (() => {
      const raw = (s as { currentSporadic?: unknown }).currentSporadic;
      if (typeof raw === "string" && raw.length > 0) return raw;
      return null;
    })(),
    // Canon ledger — legacy saves predating this field get []. Defensive:
    // anything that isn't an array becomes []. Per-entry validation is
    // light — the ledger is engine-controlled in practice, and a malformed
    // entry surfaced to the model is still better than dropping the lot.
    canon: (() => {
      const raw = (s as { canon?: unknown }).canon;
      if (!Array.isArray(raw)) return [];
      const out: CanonFact[] = [];
      for (const e of raw) {
        if (!e || typeof e !== "object") continue;
        const o = e as Partial<CanonFact>;
        if (
          typeof o.id === "string" &&
          typeof o.text === "string" &&
          typeof o.turn === "number" &&
          (o.source === "engine_bond" ||
            o.source === "engine_commitment" ||
            o.source === "engine_puzzle" ||
            o.source === "model" ||
            o.source === "series_history")
        ) {
          out.push({
            id: o.id,
            text: o.text,
            turn: o.turn,
            source: o.source,
          });
        }
      }
      return out;
    })(),
    // Content-register high-water-mark. Legacy saves missing the field
    // default to "default" — never retroactively escalate. Defensive: only
    // the literal string "mature" coerces to mature; everything else
    // (including null, unrelated strings, garbage) becomes "default".
    contentMark:
      (s as { contentMark?: unknown }).contentMark === "mature"
        ? "mature"
        : "default",
    // SERIES continuity bundle — present only on sequels. Carry it through
    // verbatim when well-formed; drop it (leave undefined) otherwise so a
    // standalone / legacy save stays standalone. Fully guarded — a malformed
    // block never throws and never half-applies.
    seriesContext: coerceSeriesContext(
      (s as { seriesContext?: unknown }).seriesContext,
    ),
  };
}

/**
 * Coerce a raw value into a well-formed SeriesContext, or undefined if it
 * isn't one. Defensive — every field guarded; an unknown structure or a
 * missing required string yields undefined (treated as "standalone, no
 * series context"). Pure.
 */
function coerceSeriesContext(raw: unknown): SeriesContext | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Partial<SeriesContext> & Record<string, unknown>;
  const VALID: SeriesStructure[] = [
    "standalone",
    "episodic",
    "arc",
    "prequel",
    "generational",
    "sidestory",
  ];
  const structure = VALID.includes(o.structure as SeriesStructure)
    ? (o.structure as SeriesStructure)
    : undefined;
  if (!structure) return undefined;
  const newTarget = typeof o.newTarget === "string" ? o.newTarget : "";
  const priorProtagonist =
    typeof o.priorProtagonist === "string" ? o.priorProtagonist : "";
  const carries = Array.isArray(o.carries)
    ? o.carries.filter((c): c is string => typeof c === "string").slice(0, 12)
    : [];
  const bookNumber =
    typeof o.bookNumber === "number" && Number.isFinite(o.bookNumber)
      ? Math.trunc(o.bookNumber)
      : 2;
  return { structure, newTarget, carries, priorProtagonist, bookNumber };
}

/**
 * BFS through the room graph assigning grid positions to rooms that don't
 * already have one. Rooms WITH a position are left alone (idempotent
 * migration). Cardinal exits (north/south/east/west) move (x,y); up/down
 * change `level` while keeping (x,y).
 *
 * Rooms unreachable from `startRoomId` via any cardinal exit get a
 * placeholder position with a unique x so the map renders them off to the
 * side instead of clobbering coords.
 */
function backfillRoomPositions(
  rooms: Record<string, Room>,
  startRoomId: string,
  startDimension: number,
  startLevel: number,
  layerSemantics: LayerSemantics,
): Record<string, Room> {
  // Work on a shallow clone of the rooms map; each room is also cloned when
  // we mutate it so the function stays pure.
  const out: Record<string, Room> = {};
  for (const [id, room] of Object.entries(rooms)) {
    out[id] = { ...room };
  }

  // If the start room doesn't have a position, plant it at (0, 0, dim, lvl).
  const start = out[startRoomId];
  if (start && !start.position) {
    start.position = {
      x: 0,
      y: 0,
      dimension: startDimension,
      level: startLevel,
    };
    if (!start.shape) start.shape = "rect";
    if (typeof start.cellSpan !== "number") start.cellSpan = 1;
    if (!start.cells) start.cells = ["0,0"];
  }

  // Helper: BFS from a single seed room, propagating positions to every
  // room reachable via cardinal exits. Used both for the main pass (starting
  // from any room with a position) and for each orphan cluster.
  function bfsFromSeed(seedId: string): void {
    const queue: string[] = [seedId];
    const seen = new Set<string>([seedId]);
    while (queue.length > 0) {
      const id = queue.shift()!;
      const room = out[id];
      if (!room || !room.position) continue;
      for (const [dir, neighbourId] of Object.entries(room.exits)) {
        if (!neighbourId) continue;
        if (!out[neighbourId]) continue;
        const v = DIRECTION_VECTORS[dir as CardinalDirection];
        if (!v) continue;
        const neighbour = out[neighbourId];
        if (!neighbour.position) {
          neighbour.position = {
            x: room.position.x + v.dx,
            y: room.position.y + v.dy,
            dimension: room.position.dimension,
            level: room.position.level + v.dLevel,
          };
          if (!neighbour.shape) neighbour.shape = "rect";
          if (typeof neighbour.cellSpan !== "number") neighbour.cellSpan = 1;
          if (!neighbour.cells) {
            neighbour.cells = [
              `${neighbour.position.x},${neighbour.position.y}`,
            ];
          }
        }
        if (!seen.has(neighbour.id)) {
          seen.add(neighbour.id);
          queue.push(neighbour.id);
        }
      }
    }
  }

  // Main pass — seed every room that already has a position (start room,
  // or rooms that already carry positions from a prior session).
  for (const room of Object.values(out)) {
    if (room.position) bfsFromSeed(room.id);
  }

  // Orphan clusters — these are typical of legacy multi-layer stories
  // where the OLD state.layer integer tracked "which floor / dimension you
  // were on" but room-to-room links across layers were never stored as
  // cardinal exits (setLayer was its own non-spatial delta). Result: each
  // historical layer is a disconnected component in the exit graph.
  //
  // Place each disconnected cluster on its own synthetic axis value:
  //   - "spatial" or "none" → bump `level` per cluster.
  //   - "dimensional"        → bump `dimension` per cluster.
  // This way the user's level/dimension switcher shows each historical
  // layer as a navigable plane instead of dumping them all at y = -1000
  // on a single shared map.
  let nextDimension = startDimension;
  let nextLevel = startLevel;
  for (const room of Object.values(out)) {
    if (room.position) continue;
    // New cluster — bump the relevant axis. Use positive increments so the
    // synthetic layers stack "above" the player's current position; users
    // intuit "more recent layers" as higher.
    if (layerSemantics === "dimensional") {
      nextDimension += 1;
    } else {
      nextLevel += 1;
    }
    room.position = {
      x: 0,
      y: 0,
      dimension:
        layerSemantics === "dimensional" ? nextDimension : startDimension,
      level: layerSemantics === "dimensional" ? startLevel : nextLevel,
    };
    if (!room.shape) room.shape = "rect";
    if (typeof room.cellSpan !== "number") room.cellSpan = 1;
    if (!room.cells) room.cells = [`${room.position.x},${room.position.y}`];
    // Spread the cluster outward by cardinal exits.
    bfsFromSeed(room.id);
  }

  // Defensive backfill for any room that had a position but missing shape
  // / cellSpan / cells (legacy saves predating those fields).
  for (const room of Object.values(out)) {
    if (!room.shape) room.shape = "rect";
    if (typeof room.cellSpan !== "number") room.cellSpan = 1;
    if (!room.cells && room.position) {
      room.cells = [`${room.position.x},${room.position.y}`];
    }
  }

  return out;
}

function clampAffinity(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(AFFINITY_MIN, Math.min(AFFINITY_MAX, n));
}

// ── Canon ledger helpers ─────────────────────────────────────────────────
//
// `humaniseMomentId` turns a snake_case moment tag ("first_kiss",
// "shared_secret") into a readable phrase ("first kiss", "shared secret")
// for the auto-emitted canon text. Pure string transform.
function humaniseMomentId(moment: string): string {
  if (typeof moment !== "string") return "a moment";
  return moment.replace(/_/g, " ").trim();
}

/**
 * Truncate canon-fact text to a sane upper bound. The schema says max
 * 200 chars; longer model emissions get clamped silently. Returns the
 * trimmed text, or null if the input wasn't a usable non-empty string.
 */
function clampCanonText(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  return trimmed.length > 200 ? trimmed.slice(0, 200) : trimmed;
}

// ── Deterministic RNG (mulberry32) ───────────────────────────────────────
//
// Pure, fast, statistically-good-enough for what we need (random block
// placement per cell). Seeded per (storyId, cellKey) so save / reload
// reproduces the same world layout.

function hashStringToSeed(input: string): number {
  // Fowler-Noll-Vo 1a. Good distribution; no deps.
  let h = 2166136261 >>> 0;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Pure block-roll. Given a room's just-created cells and the set of
 * directions NOT used as exits, returns a list of cell-keys (in
 * "x,y,dimension,level" form) the caller should mark as "blocked" in the
 * worldMask. Deterministic per (storyId, originCellKey, direction).
 *
 * `originCells` is the room's cells (in "x,y" form). For each unused
 * direction we walk one cell off the FIRST originCell in that direction
 * and, if the roll fires, splat 1..splatMax additional cells continuing in
 * the same direction. Caller is responsible for not blocking cells already
 * occupied by rooms.
 */
export function rollBlockForCells(
  storyId: string,
  origin: { x: number; y: number; dimension: number; level: number },
  unusedDirections: CardinalDirection[],
  blockChance: number,
  splatMax: number,
): string[] {
  const out: string[] = [];
  if (blockChance <= 0 || splatMax <= 0) return out;
  for (const dir of unusedDirections) {
    const v = DIRECTION_VECTORS[dir];
    if (!v) continue;
    // up/down don't have a meaningful (x, y) splat — skip; spatial block
    // semantics only apply on the horizontal plane.
    if (v.dLevel !== 0) continue;
    const seedKey =
      storyId +
      ":" +
      cellKey(origin.x, origin.y, origin.dimension, origin.level) +
      ":" +
      dir;
    const rng = mulberry32(hashStringToSeed(seedKey));
    if (rng() >= blockChance) continue;
    const splat = 1 + Math.floor(rng() * splatMax);
    for (let i = 1; i <= splat; i++) {
      const cx = origin.x + v.dx * i;
      const cy = origin.y + v.dy * i;
      out.push(cellKey(cx, cy, origin.dimension, origin.level));
    }
  }
  return out;
}

function normaliseShape(raw: unknown): RoomShape {
  if (raw === "cave" || raw === "tunnel") return raw;
  return "rect";
}

function normaliseCellSpan(raw: unknown): number {
  if (typeof raw !== "number" || !Number.isFinite(raw)) return 1;
  const n = Math.max(1, Math.min(4, Math.trunc(raw)));
  return n;
}

function normaliseDirection(raw: unknown): CardinalDirection | null {
  if (typeof raw !== "string") return null;
  if ((CARDINAL_DIRECTIONS as readonly string[]).includes(raw)) {
    return raw as CardinalDirection;
  }
  return null;
}

/**
 * Compute the cells a multi-cell room would occupy. Cells extend in the
 * direction OPPOSITE to arrivalDirection — i.e. away from the player's
 * previous room — so the room grows into the open space ahead.
 *
 * For up/down arrival, cellSpan beyond 1 doesn't make geometric sense on
 * the (x, y) plane and we collapse to span 1 at the placement position.
 */
function computeRoomCells(
  anchor: { x: number; y: number },
  arrivalDir: CardinalDirection | null,
  cellSpan: number,
): { cells: string[]; cellsXY: Array<{ x: number; y: number }> } {
  const cellsXY: Array<{ x: number; y: number }> = [{ x: anchor.x, y: anchor.y }];
  if (cellSpan <= 1) {
    return {
      cells: [`${anchor.x},${anchor.y}`],
      cellsXY,
    };
  }
  // Choose a growth direction. If arrivalDir is a horizontal cardinal, grow
  // forwards (away from where the player came); otherwise default east.
  let growDx = 1;
  let growDy = 0;
  if (arrivalDir) {
    const v = DIRECTION_VECTORS[arrivalDir];
    if (v && v.dLevel === 0) {
      growDx = v.dx;
      growDy = v.dy;
    }
  }
  for (let i = 1; i < cellSpan; i++) {
    const cx = anchor.x + growDx * i;
    const cy = anchor.y + growDy * i;
    cellsXY.push({ x: cx, y: cy });
  }
  return {
    cells: cellsXY.map(({ x, y }) => `${x},${y}`),
    cellsXY,
  };
}

/**
 * Apply a state delta returned by the model. Returns a new state object;
 * never mutates the input. Defensive against missing/malformed fields.
 *
 * `opts.scenarioId` is consulted to route the legacy `setLayer` field onto
 * `setDimension` or `setLevel` per the scenario's layerSemantics. Omit it
 * (the legacy two-arg form) and `setLayer` writes to `state.layer` only,
 * preserving the pre-2026-05-13 behaviour.
 *
 * `opts.storyId` seeds the deterministic block-roll RNG when new rooms are
 * created. Omit it and the engine uses a stable fallback ("anon") so the
 * world still builds — useful for tests.
 *
 * `opts.worldGen` carries the per-scenario blockChancePerExit + splat knob.
 * If absent we fall back to a quiet default (0.25 / 4) — the model can
 * still place rooms but the world won't get its scenario-tuned dead-ends.
 */
export interface ApplyDeltaOptions {
  scenarioId?: string | null;
  storyId?: string | null;
  worldGen?: { blockChancePerExit: number; blockSplatMaxCells: number };
}

export function applyDelta(
  state: GameState,
  delta: StateDelta | null | undefined,
  opts?: ApplyDeltaOptions,
): GameState {
  if (!delta) return state;
  const next: GameState = JSON.parse(JSON.stringify(state));
  const scenarioId = opts?.scenarioId ?? null;
  const storyId = opts?.storyId ?? "anon";
  const worldGen = opts?.worldGen ?? {
    blockChancePerExit: 0.25,
    blockSplatMaxCells: 4,
  };

  // Canon ledger — defensive coerce for in-memory states that bypassed
  // migrateState (e.g. unit-test fixtures). Older code paths may still
  // hand us a GameState without `canon`.
  if (!Array.isArray(next.canon)) next.canon = [];

  // Pending canon facts to append at the end of this delta application.
  // Each trigger pushes a partial entry; the flush block adds id (deterministic
  // `fact_<turn>_<i>`), turn = next.turnCount, and the fully-typed shape.
  const pendingCanon: Array<{
    text: string;
    source: CanonFact["source"];
  }> = [];

  if (Array.isArray(delta.newRooms)) {
    // Anchor for placement: the player's current room's position. This is
    // where arrivalDirection is measured FROM. If the current room has no
    // position (shouldn't happen post-migration, but defensive), we plant
    // at origin.
    const currentRoom = next.rooms[next.currentRoom];
    const anchorPos = currentRoom?.position ?? {
      x: 0,
      y: 0,
      dimension: next.dimension,
      level: next.level,
    };

    for (const r of delta.newRooms) {
      if (!r?.id) continue;
      if (next.rooms[r.id]) continue; // existing — no overwrite

      const arrivalDir = normaliseDirection(r.arrivalDirection);
      const shape = normaliseShape(r.shape);
      const cellSpan = normaliseCellSpan(r.cellSpan);

      // Compute the new room's anchor position from the current room +
      // arrivalDirection. Without arrivalDirection (teleport / first room),
      // anchor on the current cell — multi-cell expansion will still grow
      // east by default.
      let anchorX = anchorPos.x;
      let anchorY = anchorPos.y;
      let anchorDim = anchorPos.dimension;
      let anchorLevel = anchorPos.level;
      if (arrivalDir) {
        const v = DIRECTION_VECTORS[arrivalDir];
        anchorX += v.dx;
        anchorY += v.dy;
        anchorLevel += v.dLevel;
      }
      // Reflect the player's current D.L axes — dimensional shifts already
      // applied this turn would have updated next.dimension/level upstream;
      // we honour those here.
      anchorDim = next.dimension;
      if (!arrivalDir || DIRECTION_VECTORS[arrivalDir].dLevel === 0) {
        anchorLevel = next.level;
      }

      // Spatial-return short-circuit. The model sometimes invents a parallel
      // id for what is structurally a *return* to an existing room — same
      // anchor cell, different name. The engine can recover by inferring
      // the return: route the player to the colliding room's canonical id
      // and skip the misdirected newRoom emission entirely.
      //
      // Strictly anchor-cell only. A multi-cell span that *overflows* into
      // a different existing room is a legitimate overlap, not a return —
      // let it flow through the normal classify + leniency path. And a
      // self-collision (anchor lands on the player's current room) is
      // meaningless to "resolve" — also fall through.
      //
      // Spatial-only on purpose; no name-based fuzzy matching. Names can
      // differ slightly for the same place and fuzzy matching would
      // misroute the player into the wrong canonical room.
      const anchorKey = cellKey(anchorX, anchorY, anchorDim, anchorLevel);
      const anchorEntry = next.worldMask[anchorKey];
      if (
        typeof anchorEntry === "string" &&
        anchorEntry.startsWith("room:") &&
        anchorEntry !== `room:${r.id}` &&
        anchorEntry !== `room:${next.currentRoom}`
      ) {
        const existingId = anchorEntry.slice("room:".length);
        if (next.rooms[existingId]) {
          // eslint-disable-next-line no-console
          console.info(
            `[engine] newRoom resolved to existing room via spatial return: ${r.id} → ${existingId}`,
          );
          next.currentRoom = existingId;
          next.rooms[existingId].visited = true;
          continue;
        }
      }

      const { cells: cellKeys, cellsXY } = computeRoomCells(
        { x: anchorX, y: anchorY },
        arrivalDir,
        cellSpan,
      );

      // Classify a candidate placement. Returns:
      //   - "ok"             — every cell is open (or already room:<r.id>);
      //   - "room-collision" — at least one cell is occupied by ANOTHER room
      //                        (a real conflict — can't be cleared);
      //   - "blocked"        — no room-collision, but at least one cell is
      //                        marked "blocked" (a speculative dead-end the
      //                        block-clear fallback is allowed to overwrite).
      const classifyCells = (
        xys: Array<{ x: number; y: number }>,
      ): "ok" | "room-collision" | "blocked" => {
        let sawBlocked = false;
        for (const xy of xys) {
          const k = cellKey(xy.x, xy.y, anchorDim, anchorLevel);
          const entry = next.worldMask[k];
          if (
            typeof entry === "string" &&
            entry.startsWith("room:") &&
            entry !== `room:${r.id}`
          ) {
            return "room-collision";
          }
          if (entry === "blocked") {
            sawBlocked = true;
          }
        }
        return sawBlocked ? "blocked" : "ok";
      };

      // Final placement we'll commit. Start with the model's hint and only
      // mutate via the fallback strategies below.
      let placeXY = cellsXY;
      let placeCells = cellKeys;
      let placeSpan = cellSpan;
      const initial = classifyCells(cellsXY);

      // Track which cells (if any) we end up clearing from "blocked" so we
      // can log it. Populated only by strategy (3).
      let unblockedCount = 0;

      if (initial !== "ok") {
        // Soften the original warning — most cases now recover. The
        // distinct "abandoned after fallbacks" line below catches genuine
        // losses.
        // eslint-disable-next-line no-console
        console.warn(
          "[engine] initial placement rejected, attempting fallback:",
          r.id,
          {
            anchor: { x: anchorX, y: anchorY, dimension: anchorDim, level: anchorLevel },
            arrivalDir,
            cellSpan,
            reason: initial,
          },
        );

        // Strategy 1 — Downgrade cellSpan to 1 at the original anchor.
        // Multi-cell rooms often hit a blocked neighbour on cell 2/3 while
        // the anchor itself is clean; collapsing to a single cell rescues
        // those without moving the room.
        let recovered = false;
        if (!recovered && cellSpan > 1) {
          const candidate = computeRoomCells(
            { x: anchorX, y: anchorY },
            arrivalDir,
            1,
          );
          if (classifyCells(candidate.cellsXY) === "ok") {
            placeXY = candidate.cellsXY;
            placeCells = candidate.cells;
            placeSpan = 1;
            recovered = true;
            // eslint-disable-next-line no-console
            console.info(
              "[engine] room placement recovered via cellSpan-downgrade:",
              r.id,
            );
          }
        }

        // Strategy 2 — Slide the anchor one more cell along arrivalDirection.
        // Useful when the immediate neighbour is blocked but the next cell
        // over is free. Only meaningful for horizontal cardinals; up/down
        // arrival doesn't move on the (x,y) plane so a slide is a no-op.
        if (!recovered && arrivalDir) {
          const v = DIRECTION_VECTORS[arrivalDir];
          if (v && (v.dx !== 0 || v.dy !== 0)) {
            const slideX = anchorX + v.dx;
            const slideY = anchorY + v.dy;
            // Try the original span first; if that still collides, try span 1.
            const slideSpans: number[] = cellSpan > 1 ? [cellSpan, 1] : [1];
            for (const trySpan of slideSpans) {
              const candidate = computeRoomCells(
                { x: slideX, y: slideY },
                arrivalDir,
                trySpan,
              );
              if (classifyCells(candidate.cellsXY) === "ok") {
                // eslint-disable-next-line no-console
                console.info(
                  "[engine] room placement recovered via anchor-slide:",
                  r.id,
                  { from: { x: anchorX, y: anchorY }, to: { x: slideX, y: slideY }, trySpan },
                );
                anchorX = slideX;
                anchorY = slideY;
                placeXY = candidate.cellsXY;
                placeCells = candidate.cells;
                placeSpan = trySpan;
                recovered = true;
                break;
              }
            }
          }
        }

        // Strategy 3 — Clear offending "blocked" mask entries at the
        // original anchor/cellSpan. Block-rolls are speculative dead-ends;
        // a room the model actually emitted beats them. Only "blocked"
        // cells are cleared — room-collisions are never overwritten.
        if (!recovered) {
          const status = classifyCells(cellsXY);
          if (status === "blocked") {
            for (const xy of cellsXY) {
              const k = cellKey(xy.x, xy.y, anchorDim, anchorLevel);
              if (next.worldMask[k] === "blocked") {
                delete next.worldMask[k];
                unblockedCount++;
              }
            }
            placeXY = cellsXY;
            placeCells = cellKeys;
            placeSpan = cellSpan;
            recovered = true;
            // eslint-disable-next-line no-console
            console.info(
              "[engine] room placement recovered via block-clear:",
              r.id,
              `(unblocked ${unblockedCount} cell${unblockedCount === 1 ? "" : "s"})`,
            );
          }
        }

        if (!recovered) {
          // All three fallbacks collided with a real existing room — there
          // is no safe placement and we must drop the room. Distinct
          // warning so production logs surface the rare genuine losses
          // (vs. the noisy initial-rejection warning that usually
          // resolves).
          // eslint-disable-next-line no-console
          console.warn(
            "[engine] room placement abandoned after fallbacks:",
            r.id,
            { anchor: { x: anchorX, y: anchorY, dimension: anchorDim, level: anchorLevel }, arrivalDir, cellSpan },
          );
          continue;
        }
      }

      next.rooms[r.id] = {
        id: r.id,
        name: r.name || r.id,
        description: r.description || "",
        exits: (r.exits as Record<string, string | null>) || {},
        items: Array.isArray(r.items) ? r.items : [],
        searchedSpots: [],
        features: Array.isArray(r.features) ? r.features : [],
        visited: false,
        position: {
          x: anchorX,
          y: anchorY,
          dimension: anchorDim,
          level: anchorLevel,
        },
        shape,
        cellSpan: placeSpan,
        cells: placeCells,
      };

      // Claim cells in the worldMask.
      for (const xy of placeXY) {
        const k = cellKey(xy.x, xy.y, anchorDim, anchorLevel);
        next.worldMask[k] = `room:${r.id}`;
      }

      // Roll blocks on each cardinal direction NOT used as an exit. We
      // anchor the roll on the room's primary cell (the anchor) for
      // determinism — splatting from the anchor means save/reload
      // reproduces identical dead-ends regardless of how many cells the
      // room ended up occupying.
      const declaredExits = r.exits && typeof r.exits === "object" ? r.exits : {};
      const usedDirs = new Set<string>();
      for (const [dir, target] of Object.entries(declaredExits)) {
        if (target !== undefined) usedDirs.add(dir);
      }
      const unused = (CARDINAL_DIRECTIONS as readonly CardinalDirection[]).filter(
        (d) => !usedDirs.has(d),
      );
      const blockedCells = rollBlockForCells(
        storyId,
        { x: anchorX, y: anchorY, dimension: anchorDim, level: anchorLevel },
        unused,
        worldGen.blockChancePerExit,
        worldGen.blockSplatMaxCells,
      );
      for (const k of blockedCells) {
        // Don't blot out a room that's already there (shouldn't happen on
        // a fresh splat but defensive). Don't blot out the new room's own
        // cells, either.
        const existing = next.worldMask[k];
        if (existing === undefined) {
          next.worldMask[k] = "blocked";
        }
      }
    }
  }
  // Scenario-aware reference for downstream blocks below.
  void scenarioId;

  if (Array.isArray(delta.connectExits)) {
    // Build a set of "in-flight" newRoom ids so we don't warn about a
    // perfectly legitimate connection to a room created earlier in the
    // same delta — newRooms has already been processed into next.rooms
    // by the time we get here, but a defensive cross-check against the
    // delta's own list catches the case where a newRoom failed to
    // commit (placement abandoned after fallbacks).
    const newRoomIds = new Set<string>();
    if (Array.isArray(delta.newRooms)) {
      for (const r of delta.newRooms) {
        if (r && typeof r.id === "string") newRoomIds.add(r.id);
      }
    }
    for (const c of delta.connectExits) {
      // Phantom-room surfacing. fromRoom / toRoom must reference either
      // an existing room (already in state.rooms / next.rooms after the
      // newRooms loop above) or a room this turn's delta tried to
      // create. Anything else is the model wiring to nowhere; warn and
      // skip rather than silently dropping the dangling reference.
      const fromMissing = !next.rooms[c.fromRoom] && !newRoomIds.has(c.fromRoom);
      const toMissing = !next.rooms[c.toRoom] && !newRoomIds.has(c.toRoom);
      if (fromMissing || toMissing) {
        // eslint-disable-next-line no-console
        console.warn(
          "[engine] connectExits references phantom room: " +
            JSON.stringify({
              fromRoom: c.fromRoom,
              direction: c.direction,
              toRoom: c.toRoom,
              fromMissing,
              toMissing,
            }),
        );
        continue;
      }
      if (next.rooms[c.fromRoom]) {
        next.rooms[c.fromRoom].exits = {
          ...next.rooms[c.fromRoom].exits,
          [c.direction]: c.toRoom,
        };
      }
    }
  }

  if (Array.isArray(delta.newItems)) {
    for (const it of delta.newItems) {
      if (!it?.id) continue;
      if (!next.items[it.id]) {
        next.items[it.id] = {
          id: it.id,
          name: it.name || it.id,
          description: it.description || "",
          location: it.location || "gone",
          tags: Array.isArray(it.tags) ? it.tags : [],
        };
        if (it.location === "inventory") {
          if (!next.inventory.includes(it.id)) next.inventory.push(it.id);
        } else if (
          typeof it.location === "string" &&
          it.location.startsWith("room:")
        ) {
          const rid = it.location.slice(5);
          if (next.rooms[rid] && !next.rooms[rid].items.includes(it.id)) {
            next.rooms[rid].items.push(it.id);
          }
        }
      }
    }
  }

  if (Array.isArray(delta.moveItems)) {
    for (const m of delta.moveItems) {
      const item = next.items[m.itemId];
      if (!item) continue;
      if (item.location === "inventory") {
        next.inventory = next.inventory.filter((i) => i !== m.itemId);
      } else if (
        typeof item.location === "string" &&
        item.location.startsWith("room:")
      ) {
        const rid = item.location.slice(5);
        if (next.rooms[rid]) {
          next.rooms[rid].items = next.rooms[rid].items.filter(
            (i) => i !== m.itemId
          );
        }
      }
      item.location = m.to;
      if (m.to === "inventory") {
        if (!next.inventory.includes(m.itemId)) next.inventory.push(m.itemId);
      } else if (typeof m.to === "string" && m.to.startsWith("room:")) {
        const rid = m.to.slice(5);
        if (next.rooms[rid] && !next.rooms[rid].items.includes(m.itemId)) {
          next.rooms[rid].items.push(m.itemId);
        }
      }
    }
  }

  if (Array.isArray(delta.markSearched)) {
    for (const s of delta.markSearched) {
      const room = next.rooms[s.room];
      if (room && !room.searchedSpots.includes(s.spot)) {
        room.searchedSpots.push(s.spot);
      }
    }
  }

  if (Array.isArray(delta.newPuzzles)) {
    for (const p of delta.newPuzzles) {
      if (!p?.id) continue;
      if (!next.pendingPuzzles.find((x) => x.id === p.id)) {
        next.pendingPuzzles.push({ ...p, solved: false } as Puzzle);
      }
    }
  }

  if (Array.isArray(delta.solvePuzzles)) {
    for (const sp of delta.solvePuzzles) {
      const p = next.pendingPuzzles.find((x) => x.id === sp.id);
      // Auto-emit canon only when a puzzle ACTUALLY flips from unsolved
      // to solved this turn. Replays / unknown ids stay silent.
      const wasUnsolved = !!p && !p.solved;
      if (p) p.solved = true;
      if (wasUnsolved && p) {
        const label =
          typeof p.description === "string" && p.description.trim().length > 0
            ? p.description
            : p.id;
        pendingCanon.push({
          text: `Solved ${label}.`,
          source: "engine_puzzle",
        });
      }
      if (sp.consumeItem) {
        const it = next.items[sp.consumeItem];
        if (it) {
          if (it.location === "inventory") {
            next.inventory = next.inventory.filter(
              (i) => i !== sp.consumeItem
            );
          }
          it.location = "gone";
        }
      }
    }
  }

  if (delta.queueOrphanKey?.id) {
    if (!next.orphanKeys.find((o) => o.id === delta.queueOrphanKey!.id)) {
      next.orphanKeys.push(delta.queueOrphanKey);
    }
  }
  if (delta.queueOrphanLock?.id) {
    if (!next.orphanLocks.find((o) => o.id === delta.queueOrphanLock!.id)) {
      next.orphanLocks.push(delta.queueOrphanLock);
    }
  }

  if (delta.moveToRoom && next.rooms[delta.moveToRoom]) {
    next.currentRoom = delta.moveToRoom;
    next.rooms[delta.moveToRoom].visited = true;
  }

  // ── NPC deltas ─────────────────────────────────────────────────────────

  if (Array.isArray(delta.newNpcs)) {
    for (const n of delta.newNpcs) {
      if (!n?.id || !n?.name) continue;
      if (next.npcs[n.id]) continue; // no overwrite — once introduced, stable
      const created: Npc = {
        id: n.id,
        name: n.name,
        description: n.description || "",
        location: (n.location as NpcLocation) || "absent",
        gender: n.gender,
        archetype: n.archetype,
        affinity: clampAffinity(typeof n.affinity === "number" ? n.affinity : 0),
        knowsAbout: Array.isArray(n.knowsAbout) ? n.knowsAbout : [],
        wants: Array.isArray(n.wants) ? n.wants : [],
        mood: typeof n.mood === "string" ? n.mood : null,
        bondMoments: Array.isArray(n.bondMoments) ? n.bondMoments : [],
      };

      // ── Bundle A: Intimacy variation ──
      if (
        typeof n.disposition === "string" &&
        (NPC_DISPOSITIONS as readonly string[]).includes(n.disposition)
      ) {
        created.disposition = n.disposition as NpcDisposition;
      }
      if (
        typeof n.intimacyPreference === "string" &&
        (NPC_INTIMACY_PREFS as readonly string[]).includes(n.intimacyPreference)
      ) {
        created.intimacyPreference = n.intimacyPreference as NpcIntimacyPreference;
      }
      if (
        typeof n.ageBand === "string" &&
        (NPC_AGE_BANDS as readonly string[]).includes(n.ageBand)
      ) {
        created.ageBand = n.ageBand as NpcAgeBand;
      }
      if (
        typeof n.physique === "string" &&
        (NPC_PHYSIQUES as readonly string[]).includes(n.physique)
      ) {
        created.physique = n.physique as NpcPhysique;
      }
      if (
        typeof n.distinguishingFeature === "string" &&
        n.distinguishingFeature.trim().length > 0
      ) {
        created.distinguishingFeature = n.distinguishingFeature.trim().slice(0, 80);
      }

      // ── Bundle B: Voice + presence ──
      if (
        typeof n.speechPattern === "string" &&
        (NPC_SPEECH_PATTERNS as readonly string[]).includes(n.speechPattern)
      ) {
        created.speechPattern = n.speechPattern as NpcSpeechPattern;
      }
      if (typeof n.tell === "string" && n.tell.trim().length > 0) {
        created.tell = n.tell.trim().slice(0, 80);
      }
      if (
        typeof n.humor === "string" &&
        (NPC_HUMORS as readonly string[]).includes(n.humor)
      ) {
        created.humor = n.humor as NpcHumor;
      }

      // ── Bundle C: Narrative hooks ──
      if (
        typeof n.hiddenSecret === "string" &&
        n.hiddenSecret.trim().length > 0
      ) {
        created.hiddenSecret = n.hiddenSecret.trim().slice(0, 200);
      }
      if (typeof n.trustScar === "string" && n.trustScar.trim().length > 0) {
        created.trustScar = n.trustScar.trim().slice(0, 200);
      }
      if (typeof n.loyaltyPull === "string" && n.loyaltyPull.trim().length > 0) {
        created.loyaltyPull = n.loyaltyPull.trim().slice(0, 200);
      }

      // ── Bundle D: Emergent openness ──
      if (
        typeof n.openToShare === "string" &&
        (NPC_OPEN_TO_SHARE as readonly string[]).includes(n.openToShare)
      ) {
        created.openToShare = n.openToShare as NpcOpenToShare;
      }

      // ── Bundle E: Kink spectrum (immutable post-creation) ──
      // 0..2 entries from the controlled vocabulary. Out-of-vocabulary
      // strings are dropped silently; the array is capped at 2 to prevent
      // model drift. Empty / missing => omit the field entirely.
      if (Array.isArray(n.kinks)) {
        const kinks = (n.kinks as unknown[])
          .filter((k): k is string => typeof k === "string")
          .filter(
            (k): k is KinkValue =>
              (KINK_VALUES as readonly string[]).includes(k),
          )
          .slice(0, 2);
        if (kinks.length > 0) created.kinks = kinks;
      }

      next.npcs[n.id] = created;
    }
  }

  if (Array.isArray(delta.npcLocationChange)) {
    for (const m of delta.npcLocationChange) {
      const npc = next.npcs[m.id];
      if (!npc) continue;
      npc.location = m.to;
    }
  }

  if (Array.isArray(delta.npcAffinityDelta)) {
    // Sum nudges per NPC so a multi-entry turn (e.g. two +1s) is treated as
    // a single transition for commitment-beat detection. Capturing `before`
    // BEFORE we mutate npc.affinity is the only way to detect the
    // "crossed into +5" edge — once we write the new value it looks the
    // same as a stable +5 from a previous turn.
    const beforeByNpc = new Map<string, number>();
    for (const d of delta.npcAffinityDelta) {
      const npc = next.npcs[d.id];
      if (!npc) continue;
      if (typeof d.delta !== "number" || !Number.isFinite(d.delta)) continue;
      if (!beforeByNpc.has(d.id)) beforeByNpc.set(d.id, npc.affinity);
      npc.affinity = clampAffinity(npc.affinity + d.delta);
    }
    // Commitment beat — fires the first time affinity crosses from below
    // +5 up to +5 (or higher, via a +3 major beat). Guarded twice over:
    //   1. by the `before < AFFINITY_MAX && after >= AFFINITY_MAX` edge
    //   2. by a check against the canon ledger for an existing
    //      engine_commitment fact mentioning this npc.id, so subsequent
    //      ±0 turns at +5 never re-emit.
    // The second guard makes the canon ledger itself the dedupe surface,
    // which is robust even if affinity briefly dips and re-rises.
    for (const [npcId, before] of beforeByNpc) {
      const npc = next.npcs[npcId];
      if (!npc) continue;
      const after = npc.affinity;
      if (before >= AFFINITY_MAX) continue;
      if (after < AFFINITY_MAX) continue;
      const alreadyEmitted =
        next.canon.some(
          (f) =>
            f.source === "engine_commitment" && f.text.includes(npc.name),
        ) ||
        pendingCanon.some(
          (f) =>
            f.source === "engine_commitment" && f.text.includes(npc.name),
        );
      if (alreadyEmitted) continue;
      const playerName = next.player?.name ?? "the player";
      pendingCanon.push({
        text: `${playerName} and ${npc.name} declared their commitment.`,
        source: "engine_commitment",
      });
    }
  }

  if (Array.isArray(delta.npcKnowsAbout)) {
    for (const k of delta.npcKnowsAbout) {
      const npc = next.npcs[k.id];
      if (!npc || !Array.isArray(k.facts)) continue;
      for (const f of k.facts) {
        if (typeof f === "string" && f && !npc.knowsAbout.includes(f)) {
          npc.knowsAbout.push(f);
        }
      }
    }
  }

  if (Array.isArray(delta.npcMood)) {
    for (const m of delta.npcMood) {
      const npc = next.npcs[m.id];
      if (!npc) continue;
      npc.mood = typeof m.mood === "string" ? m.mood : null;
    }
  }

  if (Array.isArray(delta.npcWants)) {
    for (const w of delta.npcWants) {
      const npc = next.npcs[w.id];
      if (!npc || !Array.isArray(w.wants)) continue;
      npc.wants = w.wants.filter((x): x is string => typeof x === "string");
    }
  }

  // Kink-themed bondMoment kinds (model emits; engine doesn't validate):
  //   kink_bondage_discovered, kink_restraint_consent,
  //   kink_playful_ambush, kink_voyeur_witnessed, kink_brat_pushed,
  //   kink_caretaker_tucked_in, kink_taken_claimed,
  //   kink_exh_witnessed
  // Each kind fires at most once per (npc, kind) — protected by the
  // existing dedup on bondMoments.includes() below.
  if (Array.isArray(delta.addBondMoment)) {
    for (const b of delta.addBondMoment) {
      const npc = next.npcs[b.id];
      if (!npc) continue;
      if (typeof b.moment !== "string" || !b.moment) continue;
      if (!npc.bondMoments.includes(b.moment)) {
        npc.bondMoments.push(b.moment);
        // Auto-emit canon for the moment that just landed. The
        // bondMoments dedupe above ensures the canon emission only
        // fires for genuinely new beats — replays / no-ops are silent.
        const playerName = next.player?.name ?? "the player";
        pendingCanon.push({
          text: `${playerName} and ${npc.name} shared a beat: ${humaniseMomentId(b.moment)}`,
          source: "engine_bond",
        });
      }
    }
  }

  // The one mutable NPC character-depth field. No restrictions on which
  // transitions are allowed — the prompt governs the social conditions
  // for a shift; the handler is purely a setter with enum validation.
  if (Array.isArray(delta.npcShiftOpenness)) {
    for (const s of delta.npcShiftOpenness) {
      const npc = next.npcs[s.id];
      if (!npc) continue;
      if (
        typeof s.to !== "string" ||
        !(NPC_OPEN_TO_SHARE as readonly string[]).includes(s.to)
      ) {
        continue;
      }
      npc.openToShare = s.to as NpcOpenToShare;
    }
  }

  if (typeof delta.setPlayerName === "string" && delta.setPlayerName.trim()) {
    next.player = { ...next.player, name: delta.setPlayerName.trim() };
  }

  // setStoryName is an App-layer effect, not a state mutation. The story
  // name lives in stories.name on the server (surfaced via currentStory in
  // App.tsx), not in GameState. App.tsx reads result.stateDelta.setStoryName
  // from the turn response and calls renameStory() if present. We deliberately
  // do NOT clamp/normalise here — that's the App.tsx caller's responsibility.

  // Model self-adjustment of guidance level. Defensive: coerce + clamp;
  // silently leave guidance unchanged on garbage input. The manual UI
  // override always wins because the player's last action sets the level
  // in setGuidanceLevel and the model only ever nudges with this delta.
  if ("setGuidance" in delta && delta.setGuidance !== undefined) {
    const lvl = coerceGuidanceLevel(delta.setGuidance);
    if (lvl !== null) {
      const cur = next.guidance ?? { level: 0, mode: "manual" as const };
      next.guidance = { level: lvl, mode: cur.mode ?? "manual" };
    }
  }

  // Model self-adjustment of player.spice (kink-engagement dial). Clamps
  // to [0, 3] AND caps the per-turn change at ±1 (sign-only adjustment)
  // — sustained signals ramp gradually; a model misreading a single turn
  // can't jump the value 0→3. Direct admin sets via the user-menu slider
  // go through setSpiceLevel and bypass this cap.
  if ("setSpice" in delta && delta.setSpice !== undefined) {
    const target = coerceSpiceLevel(delta.setSpice);
    if (target != null) {
      const current = next.player.spice ?? 1;
      const step = Math.sign(target - current);
      if (step !== 0) {
        next.player = { ...next.player, spice: current + step };
      }
    }
  }

  // Model-emitted DIMENSIONAL shift (parallel reality axis). Defensive:
  // non-finite values ignored. Truncate to integer to match the prompt's
  // "integer" contract. Updating dimension implicitly resets the player's
  // mental model of the cell grid (each dimension has its own origin); we
  // also mirror onto state.layer so the legacy field stays in step for
  // any UI still reading it.
  if ("setDimension" in delta && delta.setDimension !== undefined) {
    const raw = delta.setDimension;
    if (typeof raw === "number" && Number.isFinite(raw)) {
      next.dimension = Math.trunc(raw);
      next.layer = next.dimension;
    }
  }
  // Model-emitted SPATIAL floor change. Cell coords carry; only `level`
  // moves. Same defensive treatment.
  if ("setLevel" in delta && delta.setLevel !== undefined) {
    const raw = delta.setLevel;
    if (typeof raw === "number" && Number.isFinite(raw)) {
      next.level = Math.trunc(raw);
      // Mirror onto state.layer for scenarios with spatial semantics so
      // pre-D.L UI reads still work; dimensional scenarios already mirror
      // via setDimension above.
      if (getLayerSemantics(scenarioId) === "spatial") {
        next.layer = next.level;
      }
    }
  }
  // Back-compat: route setLayer onto either setDimension or setLevel based
  // on the scenario's layerSemantics. This is the single source of legacy
  // compatibility — pre-2026-05-13 model output and saves both flow through
  // here. "none" scenarios update state.layer only (preserves the
  // historical behaviour of the field).
  if ("setLayer" in delta && delta.setLayer !== undefined) {
    const raw = delta.setLayer;
    if (typeof raw === "number" && Number.isFinite(raw)) {
      const v = Math.trunc(raw);
      next.layer = v;
      const semantics = getLayerSemantics(scenarioId);
      if (semantics === "spatial") {
        next.level = v;
      } else if (semantics === "dimensional") {
        next.dimension = v;
      }
    }
  }

  // ── Audio channels (Phase A) ─────────────────────────────────────────
  // Two persistent channels — ambient (continuous loop) and sporadic
  // (recurring punctuation) — both written into GameState so the model
  // sees them on the next turn via condenseStateForModel. The third
  // channel, playSfx, is a TRANSIENT signal read directly off the delta
  // by the React audio hook; engine ignores it for state purposes.
  //
  // Semantics for setAmbient / setSporadic:
  //   - non-empty string  → set to that tag
  //   - null OR empty ""  → clear to null
  //   - missing/undefined → no change
  //   - any other type    → ignored (defensive)
  if ("setAmbient" in delta && delta.setAmbient !== undefined) {
    const raw = delta.setAmbient;
    if (raw === null || raw === "") {
      next.currentAmbient = null;
    } else if (typeof raw === "string") {
      next.currentAmbient = raw;
    }
  }
  if ("setSporadic" in delta && delta.setSporadic !== undefined) {
    const raw = delta.setSporadic;
    if (raw === null || raw === "") {
      next.currentSporadic = null;
    } else if (typeof raw === "string") {
      next.currentSporadic = raw;
    }
  }
  // `playSfx` is intentionally NOT applied to state — it's a per-turn
  // signal that the React audio hook reads from the delta itself and
  // fires once. Storing it would mean the same SFX would re-fire on
  // every subsequent render until cleared. See useAudio.ts.

  // ── Canon ledger — model-emitted fact ────────────────────────────────
  // The schema gives the model a single addCanonFact per turn; we
  // append it to pendingCanon so it shares the same numbering / flush
  // path as the engine-emitted facts. Malformed input is dropped with
  // a warn so the trace is visible.
  if (delta.addCanonFact !== undefined && delta.addCanonFact !== null) {
    const fact = delta.addCanonFact as { text?: unknown };
    const clamped = clampCanonText(fact?.text);
    if (clamped) {
      pendingCanon.push({ text: clamped, source: "model" });
    } else {
      // eslint-disable-next-line no-console
      console.warn("[engine] dropped malformed addCanonFact", fact);
    }
  }

  // ── Content-register high-water-mark ─────────────────────────────────
  // Monotonic upward: the only valid value is "mature" — "default" can't
  // be re-set, and a story already at "mature" stays mature. Any other
  // input (null, garbage strings, "default") is silently ignored.
  // Server-side (routes/turn.ts) the admin gate strips setContentMark
  // from non-admin responses before they ever reach applyDelta; this
  // check is belt-and-braces for clients that load a turn response
  // outside that path (fork-replay, unit tests).
  if (delta.setContentMark === "mature" && next.contentMark !== "mature") {
    next.contentMark = "mature";
  }

  // ── Flush pending canon facts ────────────────────────────────────────
  // Monotonic append-only. Ids are deterministic — `fact_<turn>_<i>` —
  // so multiple facts emitted on the same turn don't collide. Turn is
  // next.turnCount, which is the turn the delta is being applied (not
  // incremented inside applyDelta; App.tsx bumps it).
  if (pendingCanon.length > 0) {
    const turn = typeof next.turnCount === "number" ? next.turnCount : 0;
    for (let i = 0; i < pendingCanon.length; i++) {
      const p = pendingCanon[i];
      next.canon.push({
        id: `fact_${turn}_${i}`,
        text: p.text,
        turn,
        source: p.source,
      });
    }
  }

  return next;
}

/**
 * Set the player's identity from the CharacterSetup screen (or the
 * post-setup Player-identity edit overlay). Marks setup as complete
 * whether or not gender/drawnTo were provided (skip is a valid answer —
 * falls back to gender-neutral throughout the game).
 *
 * `age` is required after setupComplete = true for newly-set-up players;
 * the helper is defensive — pass `undefined` to leave it unchanged (this
 * is how legacy saves stay valid until the user opens settings to fill
 * it in). All age fields clamp to integer [17, 90], and a reversed
 * drawn-to range is silently swapped so min <= max always holds.
 */
export function applyPlayerSetup(
  state: GameState,
  identity: {
    gender: PlayerGender;
    drawnTo: Exclude<PlayerGender, null>[] | null;
    name?: string;
    age?: number;
    drawnToAgeMin?: number;
    drawnToAgeMax?: number;
  },
): GameState {
  const clampAge = (v: unknown): number | undefined => {
    if (typeof v !== "number" || !Number.isFinite(v)) return undefined;
    return Math.max(17, Math.min(90, Math.trunc(v)));
  };
  const age = clampAge(identity.age);
  // Range defaults if caller didn't supply: pull through the current
  // state so an in-place edit that only changes age doesn't reset the
  // user's previously-narrowed range; fall back to the full span if no
  // current value either.
  let minAge =
    clampAge(identity.drawnToAgeMin) ?? clampAge(state.player.drawnToAgeMin) ?? 17;
  let maxAge =
    clampAge(identity.drawnToAgeMax) ?? clampAge(state.player.drawnToAgeMax) ?? 90;
  if (minAge > maxAge) {
    const swap = minAge;
    minAge = maxAge;
    maxAge = swap;
  }
  return {
    ...state,
    player: {
      setupComplete: true,
      gender: identity.gender,
      drawnTo:
        identity.drawnTo && identity.drawnTo.length > 0 ? identity.drawnTo : null,
      name: identity.name,
      age,
      drawnToAgeMin: minAge,
      drawnToAgeMax: maxAge,
      // Preserve any already-adapted spice level. Fresh players (no
      // prior value) start at 1 (mild).
      spice: coerceSpiceLevel(state.player.spice) ?? 1,
    },
  };
}

// ── Story-arc pacing helpers ──────────────────────────────────────────────
//
// Players reported stories drifting endlessly — currently spotted at turn 206
// with no clear ending in sight. The scenario's endGoalHints already names
// the central mystery and an "offer an ending at +5 affinity OR when the
// mystery is named aloud" trigger, but the model's adherence is soft. The
// two helpers below give the prompt structural pressure: a climax state
// machine + an estimated remaining turns hint. Engine doesn't enforce
// endings — the model still emits deathOrWin only when narrative trigger
// conditions are met. This is pacing pressure, not hard enforcement.

/**
 * Soft-target turns for a natural-paced story. Stories at or under this
 * are in early/building phases; past this they should be wrapping up.
 */
const SOFT_TARGET_TURNS = 120;

/**
 * Hard end-of-arc target. Stories beyond this are "overdue" — the model
 * should engineer an ending within the next handful of turns. No engine
 * enforcement (player agency still gates the actual deathOrWin emission).
 */
const HARD_CEILING_TURNS = 200;

export type ClimaxState = "early" | "building" | "imminent" | "overdue";

/**
 * Compute the current climax state from turnCount + story readiness.
 * Pure function; no side-effects.
 *
 * Readiness boost: a story that is genuinely satisfiable (a companion at
 * +5, OR the puzzle/mystery content complete — see computeEndingReadiness)
 * has earned an ending. Such a story is treated as AT LEAST "imminent"
 * regardless of turn count, so the ending-offer rules in the prompt's
 * STORY ARC section fire for engaged players who finish the content
 * early rather than telling them to keep wandering. This fixes the
 * proven failure mode: a turn-50 player with a +5 companion, all puzzles
 * solved, and the mystery established was stuck at "building" (offers
 * don't start until "imminent") and had no path to an ending.
 *
 * The boost only ever moves the state FORWARD (toward the ending); it
 * never pulls a late-game story back. `overdue` is preserved as the
 * turn-count backstop for the opposite failure (rambling players who
 * never satisfy a trigger).
 */
export function computeClimaxState(state: GameState): ClimaxState {
  const t = state.turnCount ?? 0;

  // Turn-count baseline.
  let base: ClimaxState;
  if (t < 65) base = "early";
  else if (t < 100) base = "building";
  else if (t < 150) base = "imminent";
  else base = "overdue";

  // Readiness boost: a satisfiable story is treated as at least
  // "imminent" so the proactive ending offer can fire. Previously this
  // was a single-tier bump keyed on +5 affinity alone, which left a
  // turn-50 player at "building" — the bug this fixes. We now floor a
  // ready story at "imminent" no matter how early it is.
  const ready = computeEndingReadiness(state).ready;
  if (ready) {
    const order: ClimaxState[] = ["early", "building", "imminent", "overdue"];
    if (order.indexOf(base) < order.indexOf("imminent")) return "imminent";
  }
  return base;
}

/**
 * Result of the ending-readiness computation. `ready` is true when the
 * story is genuinely satisfiable REGARDLESS of turn count; `reasons`
 * carries short human-readable strings naming WHY (surfaced to the model
 * so it can frame the offer in-fiction).
 */
export interface EndingReadiness {
  ready: boolean;
  reasons: string[];
}

/**
 * Compute whether the story is genuinely satisfiable — i.e. whether a
 * great ending could be offered THIS turn regardless of turn count.
 * Pure function; fully defensive (every field guarded) so a malformed
 * in-memory state never throws.
 *
 * A story is "ready" when EITHER:
 *
 *   (A) a companion has reached >= AFFINITY_MAX (+5) — the declared-bond
 *       relationship anchor. This alone earns an ending path.
 *
 *   (B) the world-mystery content is complete: ALL pending puzzles are
 *       solved, AND a central mystery has been established (heuristic: the
 *       canon ledger carries enough load-bearing facts that the world has
 *       a spine), AND every orphan lock is satisfiable (its requiresItemTag
 *       is matched by a queued orphan key OR an existing world-item tag) so
 *       there's no dangling obstacle the player can't resolve.
 *
 * The (B) path deliberately requires at least one puzzle to have existed
 * and been solved — a story with zero puzzles and an empty canon isn't
 * "complete", it's "barely started", and we don't want to offer an ending
 * on turn 2.
 */
export function computeEndingReadiness(state: GameState): EndingReadiness {
  const reasons: string[] = [];

  // ── (A) Relationship anchor: a companion at the +5 declared bond. ──
  const npcs = state.npcs && typeof state.npcs === "object" ? state.npcs : {};
  let bondedName: string | null = null;
  for (const npc of Object.values(npcs)) {
    if (!npc) continue;
    const aff = typeof npc.affinity === "number" ? npc.affinity : 0;
    if (aff >= AFFINITY_MAX) {
      bondedName = typeof npc.name === "string" && npc.name ? npc.name : "a companion";
      break;
    }
  }
  if (bondedName) {
    reasons.push(`A companion (${bondedName}) has reached a declared bond (+${AFFINITY_MAX}).`);
  }

  // ── (B) World-mystery content complete. ──
  const puzzles = Array.isArray(state.pendingPuzzles) ? state.pendingPuzzles : [];
  const unsolved = puzzles.filter((p) => p && p.solved !== true);
  const solvedCount = puzzles.length - unsolved.length;
  const allPuzzlesSolved = puzzles.length > 0 && unsolved.length === 0;

  // Central mystery established: the canon ledger has accumulated enough
  // load-bearing facts that the world has a spine to resolve. Three is the
  // floor — bond/commitment/puzzle auto-emits plus at least one model fact
  // means a real story exists, not just boot lines.
  const canon = Array.isArray(state.canon) ? state.canon : [];
  const mysteryEstablished = canon.length >= 3;

  // No orphan lock lacks a satisfiable key. A lock is satisfiable when its
  // requiresItemTag is matched by a queued orphan key's itemTag OR by the
  // tag of an item that already exists in the world (the key was planted).
  const orphanLocks = Array.isArray(state.orphanLocks) ? state.orphanLocks : [];
  const orphanKeyTags = new Set(
    (Array.isArray(state.orphanKeys) ? state.orphanKeys : [])
      .map((k) => (k && typeof k.itemTag === "string" ? k.itemTag : null))
      .filter((t): t is string => !!t),
  );
  const worldItemTags = new Set<string>();
  for (const item of Object.values(state.items ?? {})) {
    if (item && Array.isArray(item.tags)) {
      for (const tag of item.tags) {
        if (typeof tag === "string" && tag) worldItemTags.add(tag);
      }
    }
  }
  const allLocksSatisfiable = orphanLocks.every((lock) => {
    const req = lock && typeof lock.requiresItemTag === "string" ? lock.requiresItemTag : null;
    if (!req) return true; // a lock with no required tag isn't gating anything
    return orphanKeyTags.has(req) || worldItemTags.has(req);
  });

  if (allPuzzlesSolved && mysteryEstablished && allLocksSatisfiable) {
    reasons.push(
      `All ${solvedCount} puzzle(s) are solved, the central mystery is established, and no obstacle lacks a planted key.`,
    );
  }

  return { ready: reasons.length > 0, reasons };
}

/**
 * Soft estimate of how many turns the story has left in it. NOT shown
 * to the player — surfaced to the model in the condensed payload as a
 * pacing aid. Floors at 3 so the model never sees a 0 (which could
 * encourage abrupt cliff-endings).
 */
export function estimatedRemainingTurns(state: GameState): number {
  const t = state.turnCount ?? 0;
  if (t < SOFT_TARGET_TURNS) return SOFT_TARGET_TURNS - t;
  if (t < HARD_CEILING_TURNS) return Math.max(5, HARD_CEILING_TURNS - t);
  // Past the hard ceiling — wrap up in the next handful of turns.
  return 3;
}

// Send only what the model needs for this turn's decision.
// Keeps the prompt small as the world grows.
export function condenseStateForModel(state: GameState): CondensedState {
  const cur = state.rooms[state.currentRoom];
  const adjacent: CondensedState["adjacentRooms"] = {};
  if (cur) {
    for (const [dir, rid] of Object.entries(cur.exits)) {
      if (rid && state.rooms[rid]) {
        adjacent[dir] = {
          id: rid,
          name: state.rooms[rid].name,
          visited: state.rooms[rid].visited,
        };
      }
    }
  }

  const inScene: Npc[] = [];
  const known: CondensedState["npcsKnown"] = [];
  for (const npc of Object.values(state.npcs)) {
    if (
      npc.location === "with_player" ||
      npc.location === `room:${state.currentRoom}`
    ) {
      // Normalise kinks to [] so the model gets a uniform shape even for
      // legacy NPCs created before the kink field landed.
      inScene.push({ ...npc, kinks: npc.kinks ?? [] });
    } else {
      known.push({
        id: npc.id,
        name: npc.name,
        location: npc.location,
        // Raw "room:<id>" | "with_player" | "absent" so the model has
        // a hard structural anchor for where this offstage NPC is.
        currentLocation: String(npc.location ?? "absent"),
        affinity: npc.affinity,
        mood: npc.mood ?? null,
        gender: npc.gender,
        disposition: npc.disposition,
        humor: npc.humor,
        openToShare: npc.openToShare,
        // Kinks flow into offstage refs too so NPC-to-NPC observation
        // narrations (one companion privately understanding another's
        // preference after witnessing a beat) stay consistent across
        // turns even when the witnessed NPC isn't on stage right now.
        kinks: npc.kinks ?? [],
      });
    }
  }

  // Cross-room collision context. We include EVERY item (gone too — see the
  // CondensedState.worldItems doc-comment), every known room with its named
  // features, and a pre-flattened feature list. Together these let the model
  // detect duplicate "the first binding stone" / "the keeper's vow" before
  // emitting a fresh element. See PROMPTING.md "Global uniqueness".
  const worldItems: CondensedState["worldItems"] = [];
  for (const item of Object.values(state.items)) {
    if (!item || typeof item.id !== "string") continue;
    worldItems.push({
      id: item.id,
      name: item.name,
      location: item.location as string,
      tags: Array.isArray(item.tags) ? item.tags : [],
    });
  }

  const knownRooms: CondensedState["knownRooms"] = [];
  const worldNamedFeatures: CondensedState["worldNamedFeatures"] = [];
  // Defensive fallback for `position` — every modern save has it, but a
  // legacy save predating worldMask might not. Substitute the player's
  // current D.L so the payload stays well-formed.
  const fallbackDim =
    typeof state.dimension === "number" && Number.isFinite(state.dimension)
      ? state.dimension
      : 0;
  const fallbackLevel =
    typeof state.level === "number" && Number.isFinite(state.level)
      ? state.level
      : 0;
  for (const room of Object.values(state.rooms)) {
    if (!room || typeof room.id !== "string") continue;
    const features = Array.isArray(room.features) ? room.features : [];
    const roomPos = room.position ?? {
      x: 0,
      y: 0,
      dimension: fallbackDim,
      level: fallbackLevel,
    };
    // Ship a complete 6-direction exits map so the model never has to
    // distinguish "missing from object" from "not connected". Both read
    // as null on the wire.
    const rawExits =
      room.exits && typeof room.exits === "object" ? room.exits : {};
    const exits: Record<CardinalDirection, string | null> = {
      north: typeof rawExits.north === "string" ? rawExits.north : null,
      south: typeof rawExits.south === "string" ? rawExits.south : null,
      east: typeof rawExits.east === "string" ? rawExits.east : null,
      west: typeof rawExits.west === "string" ? rawExits.west : null,
      up: typeof rawExits.up === "string" ? rawExits.up : null,
      down: typeof rawExits.down === "string" ? rawExits.down : null,
    };
    knownRooms.push({
      id: room.id,
      name: room.name,
      visited: room.visited,
      features,
      position: {
        x: roomPos.x,
        y: roomPos.y,
        dimension: roomPos.dimension,
        level: roomPos.level,
      },
      exits,
    });
    for (const feature of features) {
      if (typeof feature === "string" && feature) {
        worldNamedFeatures.push({ roomId: room.id, feature });
      }
    }
  }

  // Build the neighborhood block from the current room's position. If the
  // current room has no position (only possible for an unmigrated state),
  // fall back to (0,0,dim,lvl) so the block is still well-formed.
  const dim = typeof state.dimension === "number" && Number.isFinite(state.dimension)
    ? state.dimension
    : 0;
  const lvl = typeof state.level === "number" && Number.isFinite(state.level)
    ? state.level
    : 0;
  const pos = cur?.position ?? { x: 0, y: 0, dimension: dim, level: lvl };
  const curShape: RoomShape = (cur?.shape as RoomShape) ?? "rect";
  const curCellSpan =
    typeof cur?.cellSpan === "number" && Number.isFinite(cur.cellSpan)
      ? cur.cellSpan
      : 1;
  const exits: Record<CardinalDirection, ExitInfo> = {
    north: describeExit(state, pos, "north"),
    south: describeExit(state, pos, "south"),
    east: describeExit(state, pos, "east"),
    west: describeExit(state, pos, "west"),
    up: describeExit(state, pos, "up"),
    down: describeExit(state, pos, "down"),
  };

  return {
    currentRoom: cur,
    adjacentRooms: adjacent,
    inventory: state.inventory.map((id) => state.items[id]).filter(Boolean) as Item[],
    pendingPuzzles: state.pendingPuzzles.filter((p) => !p.solved),
    orphanKeys: state.orphanKeys,
    orphanLocks: state.orphanLocks,
    worldItems,
    knownRooms,
    worldNamedFeatures,
    turnCount: state.turnCount,
    // 30 entries ≈ 15 turns of narrative memory. Tight enough to fit
    // comfortably in the variable per-turn payload (~1500 tokens), wide
    // enough that "you left Mira in the cellar 8 turns ago" stays in view.
    recentLog: state.log.slice(-30).map((e) => ({ kind: e.kind, text: e.text })),
    player: (() => {
      // Reshape the player block for the model: fold the flat
      // drawnToAgeMin/Max into a `drawnToAge: { min, max }` object, and
      // omit `age` entirely when the player has none. Range defaults
      // here mirror migrateState — a malformed state still produces a
      // well-formed condensed payload.
      const p = state.player;
      const ageRaw = p.age;
      const age =
        typeof ageRaw === "number" && Number.isFinite(ageRaw)
          ? Math.max(17, Math.min(90, Math.trunc(ageRaw)))
          : undefined;
      const minRaw = p.drawnToAgeMin;
      const maxRaw = p.drawnToAgeMax;
      let min =
        typeof minRaw === "number" && Number.isFinite(minRaw)
          ? Math.max(17, Math.min(90, Math.trunc(minRaw)))
          : 17;
      let max =
        typeof maxRaw === "number" && Number.isFinite(maxRaw)
          ? Math.max(17, Math.min(90, Math.trunc(maxRaw)))
          : 90;
      if (min > max) {
        const swap = min;
        min = max;
        max = swap;
      }
      // Spice — surface to the model so it can read the current dial
      // when deciding whether to fire a kink beat. Legacy saves missing
      // the field default to 1 (mild). The bound is enforced here too,
      // not just at migrate time, so a malformed in-memory state still
      // produces a well-formed payload.
      const spice = coerceSpiceLevel(p.spice) ?? 1;
      const out: Omit<PlayerIdentity, "drawnToAgeMin" | "drawnToAgeMax"> & {
        drawnToAge: { min: number; max: number };
      } = {
        setupComplete: p.setupComplete,
        gender: p.gender,
        drawnTo: p.drawnTo,
        name: p.name,
        drawnToAge: { min, max },
        spice,
      };
      if (typeof age === "number") out.age = age;
      return out;
    })(),
    npcsInScene: inScene,
    npcsKnown: known,
    // Send only the numeric level — the model doesn't need `mode`.
    guidance: { level: state.guidance?.level ?? 0 },
    // Generic depth/plane/tier (legacy back-compat). 0 by default.
    layer: typeof state.layer === "number" && Number.isFinite(state.layer)
      ? state.layer
      : 0,
    dimension: dim,
    level: lvl,
    neighborhood: {
      current: {
        cell: [pos.x, pos.y],
        dimension: pos.dimension ?? dim,
        level: pos.level ?? lvl,
        shape: curShape,
        cellSpan: curCellSpan,
      },
      exits,
    },
    // Phase A audio: tell the model what's already playing so it can
    // avoid pointless re-sets. playSfx is transient and intentionally
    // omitted — see StateDelta.playSfx.
    currentAmbient:
      typeof state.currentAmbient === "string" && state.currentAmbient
        ? state.currentAmbient
        : null,
    currentSporadic:
      typeof state.currentSporadic === "string" && state.currentSporadic
        ? state.currentSporadic
        : null,
    // Full canon ledger every turn. Load-bearing and append-only — never
    // expires. Legacy saves missing the field surface as []; migrateState
    // coerces on hydrate.
    canon: Array.isArray(state.canon) ? state.canon : [],
    // Content-register high-water-mark. Surface the current value so a
    // story already at "mature" keeps its register on subsequent turns
    // (within-story continuity). Legacy saves missing the field surface
    // as "default"; migrateState coerces on hydrate.
    contentMark: state.contentMark === "mature" ? "mature" : "default",
    // Story-arc pacing signals. Pure derivation from turnCount + max
    // companion affinity — see computeClimaxState / estimatedRemainingTurns.
    // Read by the prompt's STORY ARC section to tighten convergence and
    // ending-offer behaviour as the story approaches its target shape.
    arc: (() => {
      const readiness = computeEndingReadiness(state);
      // Count load-bearing canon facts, EXCLUDING series_history seeds so a
      // sequel's prior-book backstory can't mask a spineless current book.
      // Fully defensive: guard the array and each entry's source field.
      const canonFactCount = Array.isArray(state.canon)
        ? state.canon.reduce(
            (n, f) =>
              f &&
              typeof f === "object" &&
              (f as CanonFact).source !== "series_history"
                ? n + 1
                : n,
            0,
          )
        : 0;
      return {
        climaxState: computeClimaxState(state),
        estimatedRemainingTurns: estimatedRemainingTurns(state),
        endingReady: readiness.ready,
        endingReadyReasons: readiness.reasons,
        canonFactCount,
      };
    })(),
    // SERIES continuity bundle — surfaced ONLY when this story is a sequel.
    // Omitted for standalone stories so the prompt's SERIES section stays
    // inert (it's gated on the presence of this key). Re-coerced here (not
    // just at migrate time) so a malformed in-memory state still produces a
    // well-formed payload. The `series_history` canon facts ride the `canon`
    // array above untouched.
    ...(() => {
      const sc = coerceSeriesContext(state.seriesContext);
      return sc ? { seriesContext: sc } : {};
    })(),
  };
}

/**
 * Describe what's in the given cardinal direction from `origin`. Walks the
 * worldMask along that direction up to 4 cells to gauge `maxSize` for
 * open runs. Pure.
 *
 * For up/down: there's no (x, y) walk — those exits represent level changes
 * within the same column. We just consult the cell directly above/below in
 * the mask, or fall through to the room's declared exits if no mask entry
 * exists.
 */
function describeExit(
  state: GameState,
  origin: { x: number; y: number; dimension: number; level: number },
  dir: CardinalDirection,
): ExitInfo {
  const v = DIRECTION_VECTORS[dir];
  if (!v) return { kind: "open", maxSize: "small" };
  const firstX = origin.x + v.dx;
  const firstY = origin.y + v.dy;
  const firstLevel = origin.level + v.dLevel;
  const firstKey = cellKey(firstX, firstY, origin.dimension, firstLevel);
  const firstEntry = state.worldMask?.[firstKey];
  if (firstEntry === "blocked") {
    // Walking up/down: a blocked cell directly above/below means the
    // floor change isn't reachable here. Fall through to "blocked".
    if (v.dLevel !== 0) return { kind: "blocked" };
    // For horizontal: if the room across the cell is also a connected
    // existing room (via declared exits), prefer "existing" over the
    // mask's stale blocked entry. But normally a blocked cell wins.
    return { kind: "blocked" };
  }
  if (
    typeof firstEntry === "string" &&
    firstEntry.startsWith("room:")
  ) {
    return { kind: "existing", roomId: firstEntry.slice(5) };
  }
  // Defer to the current room's declared exit as a final source of truth
  // for "existing" — useful when up/down exit a stair the model wired up
  // before the mask was extended onto that level.
  const cur = state.rooms[state.currentRoom];
  const declaredTarget = cur?.exits?.[dir];
  if (declaredTarget && state.rooms[declaredTarget]) {
    return { kind: "existing", roomId: declaredTarget };
  }
  // For up/down with no mask entry and no declared exit, treat as a
  // small open opportunity — the model can build a stair if it wants.
  if (v.dLevel !== 0) return { kind: "open", maxSize: "small" };
  // Horizontal: walk the run until we hit a blocked cell or a known room.
  let openRun = 1;
  for (let i = 2; i <= 4; i++) {
    const cx = origin.x + v.dx * i;
    const cy = origin.y + v.dy * i;
    const k = cellKey(cx, cy, origin.dimension, origin.level);
    const e = state.worldMask?.[k];
    if (e === undefined) {
      openRun += 1;
      continue;
    }
    break;
  }
  let maxSize: "small" | "medium" | "large" = "small";
  if (openRun >= 4) maxSize = "large";
  else if (openRun >= 2) maxSize = "medium";
  return { kind: "open", maxSize };
}

// ── Localized meta-command aliases ───────────────────────────────────────
//
// The game generates narration in the player's language (Phase A–C i18n),
// so a Spanish-speaking player who is told "escribe 'mapa' para ver el
// mapa" should be able to type that. Equally, a Japanese player can type
// "マップ" and have it resolve to `map`.
//
// English forms always work in every language as a fallback
// (programmer-keyword-style universality) — so a multilingual player who
// has muscle-memory for the English command never gets stranded.
//
// Only the five local meta-commands handled entirely by the client are
// aliased here: map / help / restart / debug / guidance. Other commands
// (inventory, quit, signout, scenario-specific verbs) remain English-only
// for now — the dictionary is easy to extend if demand appears.
//
// In-fiction verbs ("look", "take", "go north") are NOT in this map; those
// go to the model, which interprets them in the local language.
//
// Input is normalised (trim + lowercase) before lookup. `toLowerCase()` in
// modern V8 handles Unicode correctly (Spanish "guía", Turkish dotted-i,
// etc.); scripts without case (Arabic, CJK, Hangul) are unaffected.
const COMMAND_ALIASES: Record<string, string[]> = {
  map: [
    "map", "mapa", "karte", "carte", "mappa", "kaart", "harita",
    "kort", "карта", "خريطة", "地图", "地圖", "지도", "マップ", "地図",
  ],
  help: [
    "help", "ayuda", "hilfe", "aide", "aiuto", "ajuda", "hulp",
    "yardım", "hjälp", "помощь", "مساعدة", "帮助", "幫助", "도움말",
    "ヘルプ",
  ],
  restart: [
    "restart", "reiniciar", "neustart", "redémarrer", "recommencer",
    "ricomincia", "recomeçar", "opnieuw", "yeniden başlat",
    "starta om", "перезапуск", "إعادة تشغيل", "重新开始", "重新開始",
    "다시 시작", "リスタート",
  ],
  debug: [
    "debug", "depurar", "debuggen", "déboguer", "デバッグ", "调试",
    "조사", "отладка",
  ],
  guidance: [
    "guidance", "guía", "anleitung", "guide", "guida",
    "orientação", "wegwijzer", "ガイダンス", "지침", "指引", "指南",
  ],
};

/**
 * Resolve a player's input to its canonical English meta-command keyword,
 * or null if it isn't a recognised meta-command. Input is normalised
 * (trim + lowercase) before lookup so case + whitespace don't matter.
 *
 * Only matches exact (whole-string) aliases. Sub-command forms like
 * "guidance read" or "guidance hard" are intentionally NOT matched here —
 * the caller (App.tsx) handles those via `lower.startsWith("guidance ")`
 * after the canonical resolution. To support a localised sub-command form
 * the caller can call resolveMetaCommand on the first word, then handle
 * the tail separately.
 */
export function resolveMetaCommand(input: string): string | null {
  const norm = input.trim().toLowerCase();
  if (!norm) return null;
  for (const [canonical, aliases] of Object.entries(COMMAND_ALIASES)) {
    if (aliases.includes(norm)) return canonical;
  }
  return null;
}
